import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Layout } from "../components/layout/Layout";
import { User, PatientPageData, DiagnosisCode, MedicationType, PatientFile, PatientFileType, PatientQuestionnaire, PatientSuicidalQuestHistory, MeetingType, PSYMeetingInsights, Medication, MedicalOpinion, MedicalOpinionType, CreateMedicalOpinionFileInput } from "../types/api";
import { MedicationSection } from "../components/medications/MedicationSection";
import apiService from "../services/api";
import { CustomButton } from "../components/ui/CustomButton";
import { MeetingActionBar } from "../components/ui/MeetingActionBar";
import { IssuesModal } from "../components/modals/IssuesModal";
import { EditPatientModal } from "../components/modals/EditPatientModal";
import { EditCommentsModal } from "../components/modals/EditCommentsModal";
import { SuicidalQuestionnaireModal } from "../components/modals/SuicidalQuestionnaireModal";
import { ContactDetailsModal } from "../components/modals/ContactDetailsModal";
import { PSYSummaryModal } from "../components/modals/PSYSummaryModal";
import { CMSummaryModal } from "../components/modals/CMSummaryModal";
import { NewMeetingModal } from "../components/modals/NewMeetingModal";
import { FinishTreatmentModal } from "../components/modals/FinishTreatmentModal";
import { CallPatientModal } from "../components/modals/CallPatientModal";
import { CallPatientConfModal } from "../components/modals/CallPatientConfModal";
import { AdHocMeetingModal } from "../components/modals/AdHocMeetingModal";
import { EditFileModal } from "../components/modals/EditFileModal";
import { ViewFilesModal } from "../components/modals/ViewFilesModal";
import { UploadFileModal } from "../components/modals/UploadFileModal";
import { ViewQuestionnaireModal } from "../components/modals/ViewQuestionnaireModal";
import { SuicidalHistoryTab } from "../components/SuicidalHistoryTab";
import { AppTab } from "../components/app-tab/AppTab";
import { UploadTranscriptModal } from "../components/modals/UploadTranscriptModal";
import { ViewMeetingsModal } from "../components/modals/ViewMeetingsModal";
import { CreateTaskModal } from "../components/modals/CreateTaskModal";
import { RedFlagModal } from "../components/modals/RedFlagModal";
import { NoShowModal } from "../components/modals/NoShowModal";
import { RequestDocumentsModal } from "../components/modals/RequestDocumentsModal";
import { CreateMedicalOpinionModal } from "../components/modals/CreateMedicalOpinionModal";
import { MedicalOpinionObligationApprovalModal } from "../components/modals/MedicalOpinionObligationApprovalModal";
import { MedicalOpinionLetterModal } from "../components/modals/MedicalOpinionLetterModal";
import { useMedicalOpinionTypes } from "../hooks/useMedicalOpinionOptions";
import { getMeetingTypeName, shouldStartRecallBot } from "../utils/meetingUtils";
import { WhatsAppConversationPopup } from "../components/whatsapp/WhatsAppConversationPopup";
import { useWhatsAppSSE } from "../hooks/useWhatsAppSSE";
import { normalizePhone } from "../utils/phoneUtils";
import { getTextDirection } from "../utils/textDirection";
import { PatientNotesList } from "../components/ui/PatientNotesList";
import { MobileTrackingPopup } from "../components/MobileTrackingPopup";
import { canScheduleMeeting } from '../utils/permissions';
interface PatientPageProps {
    user: User;
    onLogout: () => void;
    onNavigate?: (screen: "calendar" | "patients" | "tasks" | "internal-tasks" | "patient-page" | "patient" | "users" | "connections" | "billing" | "whatsapp" | "adhoc-meetings" | "ai-support", patientId?: string | number) => void;
    patientId: number | string;
    users: User[];
    medicationTypes: MedicationType[];
    meetingTypes: MeetingType[];
    diagnosisCodes?: DiagnosisCode[];
    patientFileTypes?: PatientFileType[];
    clinics?: any;
    taskCount?: number;
    whatsappUnreadCount?: number;
}

//tr

export const PatientPage: React.FC<PatientPageProps> = ({
    user,
    onLogout,
    onNavigate,
    patientId,
    users,
    medicationTypes,
    meetingTypes: propsMeetingTypes,
    diagnosisCodes: propsDiagnosisCodes,
    patientFileTypes: propsPatientFileTypes,
    clinics: propsClinics,
    taskCount,
    whatsappUnreadCount,
}) => {
    const [patientData, setPatientData] = useState<PatientPageData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showWhatsAppPopup, setShowWhatsAppPopup] = useState(false);
    const [patientWaUnread, setPatientWaUnread] = useState(0);
    const [showMobileTrackingPopup, setShowMobileTrackingPopup] = useState(false);
    const [showCallPatientPopup, setShowCallPatientPopup] = useState(false);
    const [isAdHocMeetingModalOpen, setIsAdHocMeetingModalOpen] = useState(false);
    const patientPhone = patientData?.phone ? normalizePhone(patientData.phone) : undefined;
    // WhatsApp is open to every user, so the live feed follows the patient's
    // phone alone rather than a per-user flag.
    useWhatsAppSSE(patientPhone, true, {
        onNewMessage: () => {
            if (!showWhatsAppPopup) {
                setPatientWaUnread((n) => n + 1);
            }
        },
    });
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'about' | 'meeting' | 'files' | 'questionnaires' | 'suicidal-history' | 'patient-history' | 'medical-opinions'>('about');
    const [isIssuesModalOpen, setIsIssuesModalOpen] = useState(false);
    // Brief "Copied" confirmation on the transfer-questionnaire link button.
    const [youthLinkCopied, setYouthLinkCopied] = useState(false);
    const [diagnosisCodes, setDiagnosisCodes] = useState<DiagnosisCode[]>(propsDiagnosisCodes || []);
    const [isEditPatientModalOpen, setIsEditPatientModalOpen] = useState(false);
    const [isSavingPatientInfo, setIsSavingPatientInfo] = useState(false);
    const [isEditNotesModalOpen, setIsEditNotesModalOpen] = useState(false);
    const [isSavingNotes, setIsSavingNotes] = useState(false);
    const [isSuicidalQuestionnaireModalOpen, setIsSuicidalQuestionnaireModalOpen] = useState(false);
    const [isContactDetailsModalOpen, setIsContactDetailsModalOpen] = useState(false);
    const [isRequestDocumentsModalOpen, setIsRequestDocumentsModalOpen] = useState(false);

    // Meetings state
    const [meetings, setMeetings] = useState<any[]>([]);
    const [isMeetingsLoading, setIsMeetingsLoading] = useState(false);
    const [selectedTherapist, setSelectedTherapist] = useState<string>('');
    const [selectedMeetingType, setSelectedMeetingType] = useState<string>('');
    const [meetingTypes, setMeetingTypes] = useState<any[]>(propsMeetingTypes || []);

    // Summary modal state
    const [isPSYSummaryModalOpen, setIsPSYSummaryModalOpen] = useState(false);
    const [isCMSummaryModalOpen, setIsCMSummaryModalOpen] = useState(false);
    const [selectedMeetingId, setSelectedMeetingId] = useState<number | undefined>(undefined);

    // Meeting details in left pane state
    const [selectedMeetingDetailsId, setSelectedMeetingDetailsId] = useState<number | null>(null);
    const [meetingForCall, setMeetingForCall] = useState<{ id: number; patient_id: number; patient_name?: string; patient_phone?: string } | null>(null);

    // CM meeting data state
    const [cmMeetingData, setCmMeetingData] = useState<{ agenda: string; agenda_object: any; comments: string } | null>(null);
    const [isCmMeetingDataLoading, setIsCmMeetingDataLoading] = useState(false);
    const [editedComments, setEditedComments] = useState<string>("");
    const [summaryHeight, setSummaryHeight] = useState(80); // percentage (20% for comments by default)
    const [isDraggingDivider, setIsDraggingDivider] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // PSY meeting data state
    const [psyMeetingData, setPsyMeetingData] = useState<PSYMeetingInsights | null>(null);
    const [isPsyMeetingDataLoading, setIsPsyMeetingDataLoading] = useState(false);
    const [editedRecommendations, setEditedRecommendations] = useState<string>("");
    const [editedSummaryAndDiscussion, setEditedSummaryAndDiscussion] = useState<string>("");
    const [psySummaryHeight, setPsySummaryHeight] = useState(65); // percentage (65% for summary, 35% for two textareas)
    const [isPsyDraggingDivider, setIsPsyDraggingDivider] = useState(false);

    // PSY agenda state
    const [psyMeetingAgenda, setPsyMeetingAgenda] = useState<string>("");
    const [isPsyAgendaLoading, setIsPsyAgendaLoading] = useState(false);
    const [isPsySaving, setIsPsySaving] = useState(false);
    const [isPsyFinishing, setIsPsyFinishing] = useState(false);
    const [isCmFinishing, setIsCmFinishing] = useState(false);

    // PSY treatment (medications and diagnosis) state
    const [psyMedications, setPsyMedications] = useState<Medication[]>([]);
    const [medicationsEnabled, setMedicationsEnabled] = useState(true);
    const [psyDiagnosis, setPsyDiagnosis] = useState<string[]>([]);
    // Per-diagnosis free-text notes, keyed by diagnosis code. Persists in
    // meeting.diagnosis_text as a JSON object { "<code>": "note", ... } whose
    // key order mirrors psyDiagnosis. If the stored value isn't valid JSON
    // we ignore it (next save overwrites with JSON).
    const [psyDiagnosisTextMap, setPsyDiagnosisTextMap] = useState<Record<string, string>>({});
    // Which diagnosis's note is currently being edited (null = none open), plus
    // the in-progress draft. The note box is only shown while editing or when a
    // saved note exists; otherwise the chip is shown on its own.
    const [editingNoteCode, setEditingNoteCode] = useState<string | null>(null);
    const [noteDraft, setNoteDraft] = useState('');
    const [diagnosisSearch, setPsyDiagnosisSearch] = useState('');
    const [showDiagnosisDropdown, setShowDiagnosisDropdown] = useState(false);

    // PSY follow-up and clinic assignment state
    const [needFollowUp, setNeedFollowUp] = useState<'yes' | 'no' | null>(null);
    const [followUpWeeks, setFollowUpWeeks] = useState<number>(0);
    const [skipCaseManager, setSkipCaseManager] = useState(false);
    const [clinicAssignment, setClinicAssignment] = useState<string>('');
    const [followUpValidationError, setFollowUpValidationError] = useState<string | null>(null);

    // Pane resize state
    const [leftPaneWidth, setLeftPaneWidth] = useState(65); // percentage
    const [isDraggingPaneDivider, setIsDraggingPaneDivider] = useState(false);
    const mainContainerRef = useRef<HTMLDivElement>(null);

    // Meeting details tab state (Summary vs Agenda vs Treatment)
    const [meetingDetailsTab, setMeetingDetailsTab] = useState<'summary' | 'agenda' | 'treatment' | 'app'>('summary');
    const [selectedSectionIndex, setSelectedSectionIndex] = useState(0);
    const [selectedSubsectionIndex, setSelectedSubsectionIndex] = useState(0);

    // Patient Meeting Summaries dropdown state
    const [patientSummaries, setPatientSummaries] = useState<Array<{ id: number; name: string; summary: string }>>([]);
    const [selectedSummaryId, setSelectedSummaryId] = useState<number | null>(null);
    const [selectedSummaryText, setSelectedSummaryText] = useState<string>("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    // Add meeting modal state
    const [isAddMeetingModalOpen, setIsAddMeetingModalOpen] = useState(false);

    // Finish treatment modal state
    const [isFinishTreatmentModalOpen, setIsFinishTreatmentModalOpen] = useState(false);

    // Warning modal state for finish meeting
    const [isFinishWarningModalOpen, setIsFinishWarningModalOpen] = useState(false);
    const [finishWarnings, setFinishWarnings] = useState<string[]>([]);
    const [finishWarningMeetingId, setFinishWarningMeetingId] = useState<number | null>(null);

    // Error modal state for finish meeting
    const [isFinishErrorModalOpen, setIsFinishErrorModalOpen] = useState(false);
    const [finishErrorMessage, setFinishErrorMessage] = useState<string>("");

    // Notification state (shows at bottom right)
    const [notification, setNotification] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    // No-show confirmation modal state
    const [isNoShowConfirmationOpen, setIsNoShowConfirmationOpen] = useState(false);

    // View meetings modal state
    const [isViewMeetingsModalOpen, setIsViewMeetingsModalOpen] = useState(false);

    // Create task modal state
    const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);

    /**
     * Popups this page can be linked straight into, as `?open=<key>`.
     *
     * Without this a link can only reach the screen, and the assistant offering
     * "open the meetings popup" had nothing to point at — the therapist landed
     * on the patient page and still had to be told which icon to press.
     *
     * Keys are the component ids the interface map uses, so a walkthrough step
     * naming a modal and a link opening it agree by construction.
     */
    const OPENABLE_MODALS: Record<string, (open: boolean) => void> = {
        ViewMeetingsModal: setIsViewMeetingsModalOpen,
        CreateTaskModal: setIsCreateTaskModalOpen,
        EditPatientModal: setIsEditPatientModalOpen,
        SuicidalQuestionnaireModal: setIsSuicidalQuestionnaireModalOpen,
        ContactDetailsModal: setIsContactDetailsModalOpen,
        RequestDocumentsModal: setIsRequestDocumentsModalOpen,
        IssuesModal: setIsIssuesModalOpen,
    };

    useEffect(() => {
        const openRequested = () => {
            const requested = new URLSearchParams(window.location.search).get("open");
            const openIt = requested ? OPENABLE_MODALS[requested] : undefined;
            if (!openIt) return;
            openIt(true);
            // Drop the parameter once it has been acted on, so the popup does not
            // reopen every time the therapist closes it and the page re-renders,
            // and so a bookmarked link is not permanently stuck open.
            const url = new URL(window.location.href);
            url.searchParams.delete("open");
            window.history.replaceState({}, "", url.toString());
        };

        openRequested();
        // Also on every later URL change. This used to run on mount alone, so a
        // link followed while ALREADY on this patient's page changed the address
        // and opened nothing -- the component never remounts, so the effect
        // never fired again. That is the common case for a link the assistant
        // offers, because it offers it while the therapist is standing here.
        window.addEventListener("popstate", openRequested);
        return () => window.removeEventListener("popstate", openRequested);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Red flag modal state
    const [isRedFlagModalOpen, setIsRedFlagModalOpen] = useState(false);

    // Files state
    const [patientFiles, setPatientFiles] = useState<PatientFile[]>([]);
    const [isFilesLoading, setIsFilesLoading] = useState(false);
    const [fileTypes, setFileTypes] = useState<PatientFileType[]>(propsPatientFileTypes || []);
    const [isEditFileModalOpen, setIsEditFileModalOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState<PatientFile | null>(null);
    const [isSavingFile, setIsSavingFile] = useState(false);
    const [isViewFilesModalOpen, setIsViewFilesModalOpen] = useState(false);
    const [isUploadFileModalOpen, setIsUploadFileModalOpen] = useState(false);
    const [isUploadingFile, setIsUploadingFile] = useState(false);

    // Questionnaires state
    const [questionnaires, setQuestionnaires] = useState<PatientQuestionnaire[]>([]);
    const [isQuestionnairesLoading, setIsQuestionnairesLoading] = useState(false);
    const [isViewQuestionnaireModalOpen, setIsViewQuestionnaireModalOpen] = useState(false);
    const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState<number | null>(null);
    const [selectedQuestionnaireTitle, setSelectedQuestionnaireTitle] = useState<string>("");

    // Suicidal questionnaire history state
    const [suicidalHistory, setSuicidalHistory] = useState<PatientSuicidalQuestHistory[]>([]);
    const [isSuicidalHistoryLoading, setIsSuicidalHistoryLoading] = useState(false);

    // Patient history state
    const [patientHistory, setPatientHistory] = useState<Array<{ id: number; created_at: number; patient_id: number; date: number; meeting_id: number; user_id: number; action: string; type: string; notes: string }>>([]);
    const [isPatientHistoryLoading, setIsPatientHistoryLoading] = useState(false);
    const [patientHistoryRefreshKey, setPatientHistoryRefreshKey] = useState(0);
    const [patientHistorySortColumn, setPatientHistorySortColumn] = useState<string>('date');
    const [patientHistorySortDirection, setPatientHistorySortDirection] = useState<'asc' | 'desc'>('desc');
    const [isPatientHistoryDetailsModalOpen, setIsPatientHistoryDetailsModalOpen] = useState(false);
    const [selectedPatientHistoryItem, setSelectedPatientHistoryItem] = useState<any | null>(null);

    // Medical opinions (מכתב לגורם חיצוני) state
    const [medicalOpinions, setMedicalOpinions] = useState<MedicalOpinion[]>([]);
    const [isMedicalOpinionsLoading, setIsMedicalOpinionsLoading] = useState(false);
    const [medicalOpinionsError, setMedicalOpinionsError] = useState<string | null>(null);
    const [medicalOpinionsRefreshKey, setMedicalOpinionsRefreshKey] = useState(0);
    const [isCreateMedicalOpinionModalOpen, setIsCreateMedicalOpinionModalOpen] = useState(false);
    const [isCreatingMedicalOpinion, setIsCreatingMedicalOpinion] = useState(false);
    const [createMedicalOpinionError, setCreateMedicalOpinionError] = useState<string | null>(null);
    const [selectedMedicalOpinion, setSelectedMedicalOpinion] = useState<MedicalOpinion | null>(null);
    const [isMedicalOpinionApprovalModalOpen, setIsMedicalOpinionApprovalModalOpen] = useState(false);
    const [isMedicalOpinionLetterModalOpen, setIsMedicalOpinionLetterModalOpen] = useState(false);

    // Which of the two medical opinion modals a row opens is decided by the request's stage,
    // not by who is looking: before approval and after closure the obligation is the whole
    // story, and from in_progress onwards the letter modal is the fuller view - it carries
    // the request details as its first tab.
    //
    // Every row is clickable for anyone who can see this page (admin, the patient's doctor,
    // the patient's case manager). Both modals are readable by all three -
    // require_medical_opinion_access is patient-access-or-assigned-doctor - and each decides
    // its own actions from the status and the viewer: the obligation decision is the admin's,
    // the letter is the assigned doctor's, and everyone else gets a read-only view.
    const openMedicalOpinionRow = (opinion: MedicalOpinion) => {
        setSelectedMedicalOpinion(opinion);
        if (opinion.status === 'in_progress' || opinion.status === 'completed') {
            setIsMedicalOpinionLetterModalOpen(true);
        } else {
            setIsMedicalOpinionApprovalModalOpen(true);
        }
    };
    // Labels for the list rows. Served by the backend and cached for the session, shared
    // with the create and approval modals.
    const { labels: medicalOpinionTypeLabels } = useMedicalOpinionTypes();

    // Same admin check as the other screens. The obligation approval action is admin-only,
    // and an admin may also write the opinion letter alongside the assigned doctor.
    const isAdmin = useMemo(() => {
        return user?.role === 'admin' || user?.role === 'Admin';
    }, [user?.role]);
    const [historyDetailsPosition, setHistoryDetailsPosition] = useState({ x: 950, y: 150 });
    const [isDraggingHistoryDetails, setIsDraggingHistoryDetails] = useState(false);
    const [dragOffsetHistoryDetails, setDragOffsetHistoryDetails] = useState({ x: 0, y: 0 });

    // Upload transcript modal state
    const [isUploadTranscriptModalOpen, setIsUploadTranscriptModalOpen] = useState(false);
    const [isUploadingTranscript, setIsUploadingTranscript] = useState(false);

    // Column resizing state for medication table
    const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
        try {
            const stored = localStorage.getItem('patient-page-medication-table-widths');
            if (stored) {
                return JSON.parse(stored);
            }
        } catch (error) {
            console.error('Failed to load medication table column widths:', error);
        }
        return {};
    });
    const [resizingColumn, setResizingColumn] = useState<string | null>(null);
    const [startX, setStartX] = useState<number>(0);
    const [startWidth, setStartWidth] = useState<number>(0);

    // Column resizing state for patient history table
    const [patientHistoryColumnWidths, setPatientHistoryColumnWidths] = useState<Record<string, number>>(() => {
        try {
            const stored = localStorage.getItem('patient-page-history-table-widths');
            if (stored) {
                return JSON.parse(stored);
            }
        } catch (error) {
            console.error('Failed to load patient history table column widths:', error);
        }
        return {};
    });
    const [patientHistoryResizingColumn, setPatientHistoryResizingColumn] = useState<string | null>(null);
    const [patientHistoryStartX, setPatientHistoryStartX] = useState<number>(0);
    const [patientHistoryStartWidth, setPatientHistoryStartWidth] = useState<number>(0);

    // Right panel collapse state
    const [isRightPanelCollapsed, setIsRightPanelCollapsed] = useState<boolean>(() => {
        try {
            const stored = localStorage.getItem('patient-page-right-panel-collapsed');
            return stored === 'true';
        } catch (error) {
            console.error('Failed to load right panel collapsed state:', error);
            return false;
        }
    });

    // Memoized callback for CM Summary Modal to prevent infinite loop
    const handleCMSummaryStatusMessage = useCallback((message: { type: 'success' | 'error' | 'loading'; text: string } | null) => {
        if (message) {
            // Show notification
            setNotification({ type: message.type === 'loading' ? 'success' : message.type, text: message.text });
            // Auto-clear non-loading messages after 3 seconds
            if (message.type !== 'loading') {
                setTimeout(() => setNotification(null), 3000);
            }
        } else {
            // Clear notification
            setNotification(null);
        }
    }, []);

    // Save right panel collapse state to localStorage
    useEffect(() => {
        try {
            localStorage.setItem('patient-page-right-panel-collapsed', String(isRightPanelCollapsed));
        } catch (error) {
            console.error('Failed to save right panel collapsed state:', error);
        }
    }, [isRightPanelCollapsed]);

    // Handle divider drag for summary/comments height adjustment
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isDraggingDivider || !containerRef.current) return;

            const container = containerRef.current;
            const containerTop = container.getBoundingClientRect().top;
            const containerHeight = container.clientHeight;

            const currentY = e.clientY - containerTop;
            const newHeight = Math.max(5, Math.min(95, (currentY / containerHeight) * 100));
            setSummaryHeight(newHeight);
        };

        const handleMouseUp = () => {
            setIsDraggingDivider(false);
        };

        if (isDraggingDivider) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDraggingDivider]);

    // Handle PSY divider drag for summary/textareas height adjustment
    useEffect(() => {
        const handlePsyMouseMove = (e: MouseEvent) => {
            if (!isPsyDraggingDivider || !containerRef.current) return;

            const container = containerRef.current;
            const containerTop = container.getBoundingClientRect().top;
            const containerHeight = container.clientHeight;

            const currentY = e.clientY - containerTop;
            const newHeight = Math.max(5, Math.min(95, (currentY / containerHeight) * 100));
            setPsySummaryHeight(newHeight);
        };

        const handlePsyMouseUp = () => {
            setIsPsyDraggingDivider(false);
        };

        if (isPsyDraggingDivider) {
            document.addEventListener('mousemove', handlePsyMouseMove);
            document.addEventListener('mouseup', handlePsyMouseUp);
            return () => {
                document.removeEventListener('mousemove', handlePsyMouseMove);
                document.removeEventListener('mouseup', handlePsyMouseUp);
            };
        }
    }, [isPsyDraggingDivider]);

    // Handle pane divider drag
    useEffect(() => {
        const handlePaneMouseMove = (e: MouseEvent) => {
            if (!isDraggingPaneDivider || !mainContainerRef.current) return;

            const container = mainContainerRef.current;
            const containerWidth = container.clientWidth;
            const currentX = e.clientX - container.getBoundingClientRect().left;
            const newWidth = Math.max(30, Math.min(70, (currentX / containerWidth) * 100));
            setLeftPaneWidth(newWidth);
        };

        const handlePaneMouseUp = () => {
            setIsDraggingPaneDivider(false);
        };

        if (isDraggingPaneDivider) {
            document.addEventListener('mousemove', handlePaneMouseMove);
            document.addEventListener('mouseup', handlePaneMouseUp);
            return () => {
                document.removeEventListener('mousemove', handlePaneMouseMove);
                document.removeEventListener('mouseup', handlePaneMouseUp);
            };
        }
    }, [isDraggingPaneDivider]);

    // Refs to prevent double-fetching in React Strict Mode
    const meetingsFetchedPatientIdRef = useRef<number | string | null>(null);
    const isInitialMountRef = useRef(true);
    const meetingIdFromUrlRef = useRef<number | null>(null);
    const cmMeetingFetchingRef = useRef<{ meetingId: number; token: string } | null>(null);
    const psyMeetingFetchingRef = useRef<{ meetingId: number; token: string } | null>(null);
    const psyAgendaFetchingRef = useRef<{ meetingId: number; token: string } | null>(null);
    const summariesFetchingRef = useRef<{ token: string; patientId: number } | null>(null);
    const meetingsFetchingRef = useRef<{ token: string; patientId: number | string } | null>(null);
    const patientPageFetchingRef = useRef<{ token: string; patientId: number | string } | null>(null);
    const lastPatientPageFetchedRef = useRef<number | string | null>(null);
    const summaryPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // Dropdown ref for click-outside handling
    const dropdownRef = useRef<HTMLDivElement>(null);

    const token = localStorage.getItem("token") || "";

    // Text direction based on clinic language
    const textDirection = patientData?.lang === "ENG" ? "ltr" : "rtl";

    // Treat both 'Kids' and 'Youth' patient types as Youth (parent info relevant).
    const isYouthPatient = patientData?.patient_type === "Youth" || patientData?.patient_type === "Kids";

    // Use clinics from props
    const clinics = useMemo(
        () => propsClinics?.items || [],
        [propsClinics?.items]
    );
    useEffect(() => {
        const fetchPatientData = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const data = await apiService.getPatientPage({
                    token,
                    patient_id: patientId,
                });
                setPatientData(data);
                // Fetch WhatsApp unread count for this patient's phone
                if (data.phone) {
                    try {
                        const normalizedPhone = normalizePhone(data.phone);
                        const waRes = await apiService.getWhatsAppConversations({
                            token,
                            search: normalizedPhone,
                        });
                        const unread = waRes.conversations.reduce((sum: number, c: any) => sum + (c.unread_count || 0), 0);
                        setPatientWaUnread(unread);
                    } catch { /* non-critical */ }
                }
            } catch (err: any) {
                console.error("Failed to load patient data:", err);
                // A 403 here is the patient-access rule, not a fault: the backend
                // allows a doctor or case manager only the patients assigned to
                // them. Say so, because "Request failed with status code 403" is
                // the network layer talking and reads as a broken page -- people
                // reload it, then report an outage.
                setError(
                    err?.response?.status === 403
                        ? "This patient is not assigned to you, so you cannot open their file. Ask the clinic admin to assign them to you if you need access."
                        : err.message || "Failed to load patient information"
                );
            } finally {
                setIsLoading(false);
            }
        };

        if (token && patientId) {
            // Check if we're already fetching for this token and patientId
            if (patientPageFetchingRef.current?.token === token && patientPageFetchingRef.current?.patientId === patientId) {
                return;
            }

            // Mark that we're fetching for these values BEFORE starting the fetch
            patientPageFetchingRef.current = { token, patientId };
            lastPatientPageFetchedRef.current = patientId;
            fetchPatientData();
        }
    }, [token, patientId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Sync diagnosis codes from props
    useEffect(() => {
        if (propsDiagnosisCodes && propsDiagnosisCodes.length > 0) {
            setDiagnosisCodes(propsDiagnosisCodes);
        }
    }, [propsDiagnosisCodes]);

    // Sync meeting types from props
    useEffect(() => {
        if (propsMeetingTypes && propsMeetingTypes.length > 0) {
            setMeetingTypes(propsMeetingTypes);
        }
    }, [propsMeetingTypes]);

    // Detect meeting_id in URL and store for immediate fetch
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const meetingIdParam = params.get('meeting_id');
        if (meetingIdParam) {
            meetingIdFromUrlRef.current = parseInt(meetingIdParam, 10);
            console.log(`[Meeting] Detected meeting_id from URL: ${meetingIdFromUrlRef.current}`);
        }
    }, []);

    // After meetings are loaded, check if there's a meeting_id in URL and set it
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const meetingIdParam = params.get('meeting_id');
        if (meetingIdParam && meetings.length > 0) {
            const meetingId = parseInt(meetingIdParam, 10);
            const meeting = meetings.find(m => m.id === meetingId);
            if (meeting) {
                setSelectedMeetingDetailsId(meetingId);
                console.log(`[Meeting] Loaded meeting ${meetingId} from URL`);

                // Set tab based on meeting type and patient type:
                // - 'about' for CM meetings (type 1)
                // - 'about' for Youth patients with meeting type 4
                // - 'meeting' for all others
                if (meeting.meetingtype_id === 1 || (meeting.patient_type === 'Youth' && meeting.meetingtype_id === 4)) {
                    setActiveTab('about');
                    console.log(`[Meeting] Set active tab to 'about' for meeting (meetingtype_id ${meeting.meetingtype_id}, patient_type ${meeting.patient_type})`);
                } else {
                    setActiveTab('meeting');
                    console.log(`[Meeting] Set active tab to 'meeting' for meeting_type_id ${meeting.meetingtype_id}`);
                }
            }
        }
    }, [meetings]);

    // Keep meeting_id in URL when selectedMeetingDetailsId changes
    useEffect(() => {
        // Skip on initial mount - let meetings load first
        if (isInitialMountRef.current) {
            isInitialMountRef.current = false;
            return;
        }

        const params = new URLSearchParams(window.location.search);
        const currentMeetingIdParam = params.get('meeting_id');
        const currentMeetingIdValue = currentMeetingIdParam ? parseInt(currentMeetingIdParam, 10) : null;

        if (selectedMeetingDetailsId && currentMeetingIdValue !== selectedMeetingDetailsId) {
            // Add or update meeting_id in URL
            params.set('meeting_id', selectedMeetingDetailsId.toString());
            window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
            console.log(`[URL] Updated meeting_id to ${selectedMeetingDetailsId}`);
        } else if (!selectedMeetingDetailsId && currentMeetingIdParam && meetings.length > 0) {
            // Only remove meeting_id from URL if meetings have loaded and user explicitly cleared it
            // If meetings are still loading, preserve the meeting_id in URL
            params.delete('meeting_id');
            const newSearch = params.toString();
            window.history.replaceState({}, '', newSearch ? `${window.location.pathname}?${newSearch}` : window.location.pathname);
            console.log(`[URL] Removed meeting_id from URL`);
        }
    }, [selectedMeetingDetailsId, meetings]);

    // Fetch CM meeting data if CM meeting is selected
    useEffect(() => {
        if (!selectedMeetingDetailsId || !meetings.length) {
            setCmMeetingData(null);
            return;
        }

        const selectedMeeting = meetings.find(m => m.id === selectedMeetingDetailsId);
        if (!selectedMeeting) {
            setCmMeetingData(null);
            return;
        }

        // Check if this is a CM meeting (type 1 or 4)
        const isCMMeeting = selectedMeeting.meetingtype_id === 1 || selectedMeeting.meetingtype_id === 4;
        if (!isCMMeeting) {
            setCmMeetingData(null);
            return;
        }

        // Check if we're already fetching for this meeting ID and token, AND we have data
        // If data is null, we should try to fetch even if we attempted before
        if (cmMeetingFetchingRef.current?.meetingId === selectedMeetingDetailsId &&
            cmMeetingFetchingRef.current?.token === token &&
            isCmMeetingDataLoading) {
            return;
        }

        // Fetch CM meeting data
        const fetchCmMeetingData = async () => {
            // Mark that we're fetching for this meeting
            cmMeetingFetchingRef.current = { meetingId: selectedMeetingDetailsId, token };
            setIsCmMeetingDataLoading(true);
            try {
                const apiUrl = localStorage.getItem('apiUrl') || process.env.REACT_APP_API_URL || 'https://dev-backend.taliaz-cm.com';
                const accessToken = token;
                const url = `${apiUrl}/get_cm_meeting_data?token=${accessToken}&meeting_id=${selectedMeetingDetailsId}`;

                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error(`Failed to fetch CM meeting data: ${response.statusText}`);
                }

                const data = await response.json();
                setCmMeetingData(data);
                console.log(`[CM Meeting] Loaded CM meeting data for meeting ${selectedMeetingDetailsId}`);
            } catch (err: any) {
                console.error("Failed to load CM meeting data:", err);
                setCmMeetingData(null);
            } finally {
                setIsCmMeetingDataLoading(false);
            }
        };

        fetchCmMeetingData();
    }, [selectedMeetingDetailsId, meetings, token]); // eslint-disable-line react-hooks/exhaustive-deps

    // Sync edited comments with CM meeting data
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (cmMeetingData) {
            setEditedComments(cmMeetingData.comments || "");
        }
    }, [cmMeetingData]);

    // Reset divider heights when meeting changes
    useEffect(() => {
        setSummaryHeight(80);
        setPsySummaryHeight(65);
        setMeetingDetailsTab('summary');
    }, [selectedMeetingDetailsId]);

    // Fetch PSY meeting data if PSY meeting is selected
    useEffect(() => {
        if (!selectedMeetingDetailsId || !meetings.length) {
            setPsyMeetingData(null);
            return;
        }

        const selectedMeeting = meetings.find(m => m.id === selectedMeetingDetailsId);
        if (!selectedMeeting) {
            setPsyMeetingData(null);
            return;
        }

        // Check if this is a PSY meeting (type 2 or 3)
        const isPSYMeeting = selectedMeeting.meetingtype_id === 2 || selectedMeeting.meetingtype_id === 3;
        if (!isPSYMeeting) {
            setPsyMeetingData(null);
            return;
        }

        // Check if we're already fetching for this meeting ID and token, AND we're actively loading
        // If data is null, we should try to fetch even if we attempted before
        if (psyMeetingFetchingRef.current?.meetingId === selectedMeetingDetailsId &&
            psyMeetingFetchingRef.current?.token === token &&
            isPsyMeetingDataLoading) {
            return;
        }

        // Fetch PSY meeting data
        const fetchPsyMeetingData = async () => {
            psyMeetingFetchingRef.current = { meetingId: selectedMeetingDetailsId, token };
            setIsPsyMeetingDataLoading(true);
            try {
                const data = await apiService.getPSYMeetingInsights({
                    token,
                    meeting_id: selectedMeetingDetailsId,
                });

                // Medications and diagnosis come from patient_page_meetings (selectedMeeting)
                // Not from getPSYMeetingInsights. Merge the data:
                const mergedData = {
                    ...data,
                    medications: selectedMeeting.medications || [],
                    diagnosis: selectedMeeting.diagnosis || '',
                    diagnosis_text: selectedMeeting.diagnosis_text || '',
                };
                setPsyMeetingData(mergedData);
                console.log(`[PSY Meeting] Loaded PSY meeting insights for meeting ${selectedMeetingDetailsId}`);
            } catch (err: any) {
                console.error("Failed to load PSY meeting insights:", err);
                setPsyMeetingData(null);
            } finally {
                setIsPsyMeetingDataLoading(false);
            }
        };

        fetchPsyMeetingData();
    }, [selectedMeetingDetailsId, meetings, token]); // eslint-disable-line react-hooks/exhaustive-deps

    // Transform API medication response to new Medication interface
    const transformApiMedications = (medications: any[]): Medication[] => {
        if (!medications || !Array.isArray(medications)) {
            return [];
        }

        const transformed = medications.map(med => {
            const result = {
                id: med.id,
                created_at: med.created_at,
                updated_at: med.updated_at,
                created_by: med.created_by,
                patient_id: med.patient_id,
                meeting_id: med.meeting_id,
                medication_type_id: med.medication_type_id || 0,
                name: med.name || '',
                // Handle both old and new field names
                dosage: med.dosage || med.dosage_amount?.toString() || '0',
                unit: med.unit || med.dosage_unit || '',
                per_day: med.per_day || '',
                how_many_days: med.how_many_days || '',
                amount_per_take: med.amount_per_take || 0,
                amount: typeof med.amount === 'string' ? parseFloat(med.amount) : (med.amount || med.freq_amount || 0),
                amount_per: med.amount_per || med.freq_period || 'Day',
                duration: typeof med.duration === 'string' ? parseInt(med.duration, 10) : (med.duration || med.duration_in_days || 0),
                number_of_packages: med.number_of_packages || 0,
                likelihood_of_med_in_predictix: med.likelihood_of_med_in_predictix || '',
                start_date: med.start_date || null,
                comments: med.comments || med.notes || '',
            };
            return result;
        });
        return transformed;
    };

    // Sync edited fields, summary, medications and diagnosis with PSY meeting data
    useEffect(() => {
        if (psyMeetingData) {
            setEditedRecommendations(psyMeetingData.recommendations || "");
            setEditedSummaryAndDiscussion(psyMeetingData.summary_and_discussion || "");
            setSelectedSummaryText(psyMeetingData.summary || "");

            // Transform and set medications
            const transformedMedications = transformApiMedications(psyMeetingData.medications || []);
            setPsyMedications(transformedMedications);

            // Parse comma-separated diagnosis string into array
            const diagnosisArray = psyMeetingData.diagnosis
                ? psyMeetingData.diagnosis.split(',').map(d => d.trim()).filter(d => d)
                : [];
            setPsyDiagnosis(diagnosisArray);

            // Parse diagnosis_text as JSON map. If it's not valid JSON object, ignore.
            try {
                const parsed = JSON.parse(psyMeetingData.diagnosis_text || '{}');
                if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                    setPsyDiagnosisTextMap(parsed as Record<string, string>);
                } else {
                    setPsyDiagnosisTextMap({});
                }
            } catch {
                setPsyDiagnosisTextMap({});
            }
        }
    }, [psyMeetingData]);

    // Fetch PSY meeting agenda if PSY meeting is selected
    useEffect(() => {
        if (!selectedMeetingDetailsId || !meetings.length) {
            setPsyMeetingAgenda("");
            // Reset follow-up state when no meeting is selected
            setNeedFollowUp(null);
            setFollowUpWeeks(0);
            setSkipCaseManager(false);
            setClinicAssignment("");
            return;
        }

        const selectedMeeting = meetings.find(m => m.id === selectedMeetingDetailsId);
        if (!selectedMeeting) {
            setPsyMeetingAgenda("");
            // Reset follow-up state if meeting not found
            setNeedFollowUp(null);
            setFollowUpWeeks(0);
            setSkipCaseManager(false);
            setClinicAssignment("");
            return;
        }

        // Check if this is a PSY meeting (type 2 or 3)
        const isPSYMeeting = selectedMeeting.meetingtype_id === 2 || selectedMeeting.meetingtype_id === 3;
        if (!isPSYMeeting) {
            setPsyMeetingAgenda("");
            return;
        }

        // Initialize follow-up state from selectedMeeting
        setNeedFollowUp(selectedMeeting.yes_followups ? 'yes' : selectedMeeting.no_followups ? 'no' : null);
        setFollowUpWeeks(selectedMeeting.need_followup_in_weeks || 0);
        setSkipCaseManager(selectedMeeting.skip_casemanger_followup || false);
        setClinicAssignment(selectedMeeting.clinic_assignment || "");

        // Check if we're already fetching for this meeting ID and token
        if (psyAgendaFetchingRef.current?.meetingId === selectedMeetingDetailsId && psyAgendaFetchingRef.current?.token === token) {
            return;
        }

        // Fetch PSY meeting agenda
        const fetchPsyMeetingAgenda = async () => {
            psyAgendaFetchingRef.current = { meetingId: selectedMeetingDetailsId, token };
            setIsPsyAgendaLoading(true);
            try {
                const data = await apiService.getPSYMeetingAgenda({
                    token,
                    meeting_id: selectedMeetingDetailsId,
                });
                setPsyMeetingAgenda((data.message || "").replace(/\\n/g, '\n'));
                console.log(`[PSY Meeting] Loaded PSY meeting agenda for meeting ${selectedMeetingDetailsId}`);
            } catch (err: any) {
                console.error("Failed to load PSY meeting agenda:", err);
                setPsyMeetingAgenda("");
            } finally {
                setIsPsyAgendaLoading(false);
            }
        };

        fetchPsyMeetingAgenda();
    }, [selectedMeetingDetailsId, meetings, token]);

    // Fetch patient meeting summaries
    useEffect(() => {
        const fetchPatientSummaries = async () => {
            try {
                const summaries = await apiService.getPatientMeetingSummaries(token, patientId);
                setPatientSummaries(summaries);
                // Set first summary as default
                if (summaries.length > 0) {
                    setSelectedSummaryId(summaries[0].id);
                    setSelectedSummaryText(summaries[0].summary);
                }
            } catch (err: any) {
                console.error("Failed to load patient meeting summaries:", err);
                setPatientSummaries([]);
            }
        };

        if (token && patientId) {
            const patientIdNum = typeof patientId === 'string' ? parseInt(patientId, 10) : patientId;
            // Check if we're already fetching with these exact values
            if (summariesFetchingRef.current?.token === token && summariesFetchingRef.current?.patientId === patientIdNum) {
                return;
            }
            // Mark that we're fetching for these values BEFORE starting the fetch
            summariesFetchingRef.current = { token, patientId: patientIdNum };
            fetchPatientSummaries();
        }
    }, [token, patientId]);

    // Auto-select summary matching the selected meeting
    useEffect(() => {
        if (selectedMeetingDetailsId && patientSummaries.length > 0) {
            // Find the summary with matching meeting id
            const matchingSummary = patientSummaries.find(s => s.id === selectedMeetingDetailsId);
            if (matchingSummary) {
                setSelectedSummaryId(matchingSummary.id);
                setSelectedSummaryText(matchingSummary.summary);
                console.log(`[Meeting] Auto-selected summary for meeting ${selectedMeetingDetailsId}`);
            } else if (patientSummaries.length > 0) {
                // If no matching summary, select the first one
                setSelectedSummaryId(patientSummaries[0].id);
                setSelectedSummaryText(patientSummaries[0].summary);
                console.log(`[Meeting] No matching summary for meeting ${selectedMeetingDetailsId}, selected first summary`);
            }
        }
    }, [selectedMeetingDetailsId, patientSummaries]);

    // Handle click outside dropdown to close it
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        if (isDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [isDropdownOpen]);

    useEffect(() => {
        const fetchMeetings = async () => {
            // Fetch meetings when on meeting tab, patient-history tab, or if meeting_id is in URL
            if (!token || !patientId) return;

            const shouldFetch = activeTab === 'meeting' || activeTab === 'patient-history' || meetingIdFromUrlRef.current !== null;
            if (!shouldFetch) return;

            // Check if we're already fetching for this token and patientId
            if (meetingsFetchingRef.current?.token === token && meetingsFetchingRef.current?.patientId === patientId) {
                console.log(`[Meetings] Already fetching for patient ${patientId}`);
                return;
            }

            if (activeTab === 'meeting') {
                setIsMeetingsLoading(true);
            }

            // Mark that we're fetching for this token and patientId
            meetingsFetchingRef.current = { token, patientId };

            try {
                const meetingsData = await apiService.getPatientMeetings(token, patientId);
                setMeetings(meetingsData);
                meetingsFetchedPatientIdRef.current = patientId;
                console.log(`[Meetings] Loaded ${meetingsData.length} meetings for patient ${patientId}`);
            } catch (err: any) {
                console.error("Failed to load meetings:", err);
            } finally {
                if (activeTab === 'meeting') {
                    setIsMeetingsLoading(false);
                }
            }
        };

        fetchMeetings();
    }, [token, patientId, activeTab]);

    // Fetch files when Files tab is active
    useEffect(() => {
        const fetchFiles = async () => {
            if (!token || !patientId || activeTab !== 'files') return;

            setIsFilesLoading(true);
            try {
                const filesData = await apiService.getPatientFiles({ token, patient_id: patientId });
                setPatientFiles(filesData);
            } catch (err: any) {
                console.error("Failed to load patient files:", err);
            } finally {
                setIsFilesLoading(false);
            }
        };

        fetchFiles();
    }, [token, patientId, activeTab]);

    // Sync file types from props
    useEffect(() => {
        if (propsPatientFileTypes && propsPatientFileTypes.length > 0) {
            setFileTypes(propsPatientFileTypes);
        }
    }, [propsPatientFileTypes]);

    // Fetch questionnaires when Questionnaires tab is active
    useEffect(() => {
        const fetchQuestionnaires = async () => {
            if (!token || !patientId || activeTab !== 'questionnaires') return;

            setIsQuestionnairesLoading(true);
            try {
                const questionnairesData = await apiService.getPatientQuestionnaires({ token, patient_id: patientId });
                setQuestionnaires(questionnairesData);
            } catch (err: any) {
                console.error("Failed to load questionnaires:", err);
            } finally {
                setIsQuestionnairesLoading(false);
            }
        };

        fetchQuestionnaires();
    }, [token, patientId, activeTab]);

    // Fetch suicidal questionnaire history when Suicidal History tab is active
    useEffect(() => {
        const fetchSuicidalHistory = async () => {
            if (!token || !patientId || activeTab !== 'suicidal-history') return;

            setIsSuicidalHistoryLoading(true);
            try {
                const historyData = await apiService.getPatientSuicidalQuestHistory({ token, patient_id: patientId });
                setSuicidalHistory(historyData);
            } catch (err: any) {
                console.error("Failed to load suicidal questionnaire history:", err);
            } finally {
                setIsSuicidalHistoryLoading(false);
            }
        };

        fetchSuicidalHistory();
    }, [token, patientId, activeTab]);

    // Fetch patient history when Patient History tab is active
    useEffect(() => {
        const fetchPatientHistoryData = async () => {
            if (!token || !patientId || activeTab !== 'patient-history') return;

            setIsPatientHistoryLoading(true);
            try {
                const historyData = await apiService.getPatientHistory(token, patientId);
                setPatientHistory(historyData);
            } catch (err: any) {
                console.error("Failed to load patient history:", err);
            } finally {
                setIsPatientHistoryLoading(false);
            }
        };

        fetchPatientHistoryData();
    }, [token, patientId, activeTab, patientHistoryRefreshKey]);

    // Fetch medical opinions when the Medical Opinions tab is active
    useEffect(() => {
        const fetchMedicalOpinions = async () => {
            if (!token || !patientId || activeTab !== 'medical-opinions') return;

            setIsMedicalOpinionsLoading(true);
            try {
                const opinions = await apiService.getMedicalOpinions({
                    token,
                    patient_id: Number(patientId),
                });
                setMedicalOpinions(opinions);
            } catch (err: any) {
                console.error("Failed to load medical opinions:", err);
                setMedicalOpinionsError(
                    err?.response?.status === 403
                        ? 'You are not authorized to view medical opinions for this patient'
                        : 'Failed to load medical opinions'
                );
            } finally {
                setIsMedicalOpinionsLoading(false);
            }
        };

        fetchMedicalOpinions();
    }, [token, patientId, activeTab, medicalOpinionsRefreshKey]);

    const handleCreateMedicalOpinion = async (data: {
        opinion_type: MedicalOpinionType;
        obligation: number;
        initiator_notes: string;
        files: CreateMedicalOpinionFileInput[];
    }) => {
        if (!token || !patientId) return;

        setIsCreatingMedicalOpinion(true);
        setCreateMedicalOpinionError(null);
        try {
            const result = await apiService.createMedicalOpinion({
                token,
                patient_id: Number(patientId),
                ...data,
            });
            // A business-rule rejection arrives as HTTP 200 with status "error" - keep the
            // modal open with the message so the user can correct the input.
            if (result?.status === 'error') {
                setCreateMedicalOpinionError(result.message || 'Failed to create the request');
                return;
            }
            setIsCreateMedicalOpinionModalOpen(false);
            setMedicalOpinionsRefreshKey((k) => k + 1);
        } catch (err: any) {
            // Authorization failures are 403 with a detail body, not the status/message shape.
            setCreateMedicalOpinionError(
                err?.response?.status === 403
                    ? 'You are not authorized to create a medical opinion request for this patient'
                    : err?.message || 'Failed to create the request'
            );
        } finally {
            setIsCreatingMedicalOpinion(false);
        }
    };

    // The red dot reflects a request still awaiting work. patientData carries the flag on
    // page load; once the tab has been opened we also derive it from the list, so the dot
    // appears immediately after a request is created without refetching the patient page.
    const hasOpenMedicalOpinion =
        patientData?.has_open_medical_opinion ||
        medicalOpinions.some(
            (o) => o.status === 'pending_obligation' || o.status === 'in_progress'
        );

    // Save column widths to localStorage whenever they change

    useEffect(() => {
        try {
            localStorage.setItem('patient-page-medication-table-widths', JSON.stringify(columnWidths));
        } catch (error) {
            console.error('Failed to save medication table column widths:', error);
        }
    }, [columnWidths]);

    // Column resize handlers for medication table
    const handleResizeStart = (e: React.MouseEvent, column: string, currentWidth: number) => {
        e.preventDefault();
        e.stopPropagation();
        setResizingColumn(column);
        setStartX(e.clientX);
        setStartWidth(currentWidth);
    };

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!resizingColumn) return;

            const diff = e.clientX - startX;
            const newWidth = Math.max(50, startWidth + diff); // Minimum width of 50px

            setColumnWidths(prev => ({
                ...prev,
                [resizingColumn]: newWidth
            }));
        };

        const handleMouseUp = () => {
            setResizingColumn(null);
        };

        if (resizingColumn) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [resizingColumn, startX, startWidth]);

    // Save patient history column widths to localStorage
    useEffect(() => {
        try {
            localStorage.setItem('patient-page-history-table-widths', JSON.stringify(patientHistoryColumnWidths));
        } catch (error) {
            console.error('Failed to save patient history table column widths:', error);
        }
    }, [patientHistoryColumnWidths]);

    // Column resize handlers for patient history table
    const handleHistoryResizeStart = (e: React.MouseEvent, column: string, currentWidth: number) => {
        e.preventDefault();
        e.stopPropagation();

        // Get the actual width from the element if possible
        const element = (e.target as HTMLElement).closest('th');
        const actualWidth = element ? element.offsetWidth : currentWidth;

        setPatientHistoryResizingColumn(column);
        setPatientHistoryStartX(e.clientX);
        setPatientHistoryStartWidth(actualWidth);
        console.log(`[Resize] Column: ${column}, Start X: ${e.clientX}, Start Width: ${actualWidth}`);
    };

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!patientHistoryResizingColumn) return;

            const diff = e.clientX - patientHistoryStartX;
            const newWidth = Math.max(50, patientHistoryStartWidth + diff);

            console.log(`[Resize Move] Column: ${patientHistoryResizingColumn}, Current X: ${e.clientX}, Diff: ${diff}, New Width: ${newWidth}`);

            setPatientHistoryColumnWidths(prev => ({
                ...prev,
                [patientHistoryResizingColumn]: newWidth
            }));
        };

        const handleMouseUp = () => {
            console.log(`[Resize End] Column: ${patientHistoryResizingColumn}`);
            setPatientHistoryResizingColumn(null);
        };

        if (patientHistoryResizingColumn) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [patientHistoryResizingColumn, patientHistoryStartX, patientHistoryStartWidth]);

    // Handle dragging history details panel
    const handleHistoryDetailsHeaderMouseDown = (e: React.MouseEvent) => {
        setIsDraggingHistoryDetails(true);
        setDragOffsetHistoryDetails({
            x: e.clientX - historyDetailsPosition.x,
            y: e.clientY - historyDetailsPosition.y
        });
    };

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isDraggingHistoryDetails) return;

            setHistoryDetailsPosition({
                x: e.clientX - dragOffsetHistoryDetails.x,
                y: e.clientY - dragOffsetHistoryDetails.y
            });
        };

        const handleMouseUp = () => {
            setIsDraggingHistoryDetails(false);
        };

        if (isDraggingHistoryDetails) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.userSelect = 'none';
            document.body.style.cursor = 'move';

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
                document.body.style.userSelect = 'auto';
                document.body.style.cursor = 'auto';
            };
        }
    }, [isDraggingHistoryDetails, dragOffsetHistoryDetails]);

    // Cleanup polling interval on unmount
    useEffect(() => {
        return () => {
            if (summaryPollingIntervalRef.current) {
                clearInterval(summaryPollingIntervalRef.current);
                summaryPollingIntervalRef.current = null;
            }
        };
    }, []);

    const formatDate = (timestamp: number | undefined | null): string => {
        if (!timestamp || timestamp === 0) return "-";
        const date = new Date(timestamp);
        return date.toLocaleDateString("en-GB");
    };

    const handleEditFile = (file: PatientFile) => {
        setSelectedFile(file);
        setIsEditFileModalOpen(true);
    };

    const handleSaveFile = async (fileId: number, description: string, fileTypeId: number) => {
        setIsSavingFile(true);
        try {
            await apiService.updatePatientFile({
                token,
                file_id: fileId,
                description,
                patient_file_type_id: fileTypeId,
            });
            // Refresh files list
            if (patientId) {
                const filesData = await apiService.getPatientFiles({ token, patient_id: patientId });
                setPatientFiles(filesData);
            }
            setIsEditFileModalOpen(false);
        } catch (err: any) {
            console.error("Failed to save file:", err);
            throw err;
        } finally {
            setIsSavingFile(false);
        }
    };

    const handleDownloadFile = async (fileId: string, filename: string) => {
        try {
            await apiService.downloadFile(fileId, filename);
        } catch (err: any) {
            console.error("Failed to download file:", err);
            alert("Failed to download file: " + (err.message || 'Unknown error'));
        }
    };

    const handleUploadFile = async (filename: string, fileTypeId: number, base64file: string, comments: string) => {
        setIsUploadingFile(true);
        try {
            await apiService.uploadPatientFile({
                token,
                patient_id: Number(patientId),
                filename,
                file_type: fileTypeId,
                base64file,
                comments,
            });
            // Refresh files list
            if (patientId) {
                const filesData = await apiService.getPatientFiles({ token, patient_id: patientId });
                setPatientFiles(filesData);
            }
            setIsUploadFileModalOpen(false);
        } catch (err: any) {
            console.error("Failed to upload file:", err);
            throw err;
        } finally {
            setIsUploadingFile(false);
        }
    };

    const handleUploadTranscript = async (meetingId: number, filename: string, text: string) => {
        setIsUploadingTranscript(true);
        try {
            await apiService.uploadTranscript({
                token,
                meeting_id: meetingId,
                filename,
                text,
            });
            // Close the modal on success
            setIsUploadTranscriptModalOpen(false);
        } catch (err: any) {
            console.error("Failed to upload transcript:", err);
            throw err;
        } finally {
            setIsUploadingTranscript(false);
        }
    };

    const formatDateTime = (timestamp: number | undefined | null): string => {
        if (!timestamp || timestamp === 0) return "-";
        const date = new Date(timestamp);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}`;
    };

    const formatTime = (timestamp: number | undefined | null): string => {
        if (!timestamp || timestamp === 0) return "-";
        const date = new Date(timestamp);
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${hours}:${minutes}`;
    };

    // PSY Treatment handlers
    const handleAddDiagnosis = (code: string) => {
        if (!psyDiagnosis.includes(code)) {
            setPsyDiagnosis([...psyDiagnosis, code]);
        }
        setPsyDiagnosisSearch('');
        setShowDiagnosisDropdown(false);
    };

    const handleRemoveDiagnosis = (code: string) => {
        setPsyDiagnosis(psyDiagnosis.filter(d => d !== code));
        // Also drop any per-diagnosis note for the removed code
        setPsyDiagnosisTextMap(prev => {
            const { [code]: _removed, ...rest } = prev;
            return rest;
        });
        if (editingNoteCode === code) {
            setEditingNoteCode(null);
            setNoteDraft('');
        }
    };

    // Open the inline note editor for a diagnosis, seeding the draft with the
    // existing note (if any).
    const handleStartEditNote = (code: string) => {
        setNoteDraft(psyDiagnosisTextMap[code] || '');
        setEditingNoteCode(code);
    };

    // Commit the draft into the note map (dropping the entry when empty) and
    // close the editor. The note is persisted to the server with the rest of
    // the meeting on the main Save.
    const handleSaveNote = (code: string) => {
        const text = noteDraft.trim();
        setPsyDiagnosisTextMap(prev => {
            if (text) return { ...prev, [code]: text };
            const { [code]: _removed, ...rest } = prev;
            return rest;
        });
        setEditingNoteCode(null);
        setNoteDraft('');
    };

    const handleCancelEditNote = () => {
        setEditingNoteCode(null);
        setNoteDraft('');
    };

    // Clear a saved note without removing the diagnosis itself.
    const handleRemoveNote = (code: string) => {
        setPsyDiagnosisTextMap(prev => {
            const { [code]: _removed, ...rest } = prev;
            return rest;
        });
    };

    // Serialize the per-diagnosis text map back into diagnosis_text JSON, in
    // the order of psyDiagnosis. Empty notes are dropped. Returns "" if no
    // entries remain (avoids saving "{}").
    const serializeDiagnosisText = (): string => {
        const ordered: Record<string, string> = {};
        for (const code of psyDiagnosis) {
            const text = (psyDiagnosisTextMap[code] || '').trim();
            if (text) ordered[code] = text;
        }
        return Object.keys(ordered).length > 0 ? JSON.stringify(ordered) : '';
    };

    const getFilteredDiagnosisCodes = () => {
        if (!diagnosisCodes) return [];
        if (!diagnosisSearch) return diagnosisCodes.slice(0, 50);
        const search = diagnosisSearch.toLowerCase();
        return diagnosisCodes
            .filter(d =>
                d.code.toLowerCase().includes(search) ||
                d.Diagnosis.toLowerCase().includes(search)
            )
            .slice(0, 50);
    };

    // ResizableHeader component for medication table
    // =====================================================
    // UNIFIED PSY MEETING HANDLERS - Used by all 3 tabs
    // =====================================================

    // Re-fetch the patient meetings so the cached diagnosis/medications/notes
    // reflect the latest saved values (the merge effect sources these from the
    // meetings array, which is otherwise only fetched on initial load).
    const refreshMeetings = async () => {
        try {
            const meetingsData = await apiService.getPatientMeetings(token, patientId);
            setMeetings(meetingsData);
        } catch (refreshErr) {
            console.error('[Meetings] Failed to refresh meetings:', refreshErr);
        }
    };

    const handlePSYSave = async () => {
        if (!selectedMeetingDetailsId || !patientData?.id) {
            console.error('[PSY Meeting] Missing meeting or patient ID');
            return;
        }

        if (needFollowUp === 'yes' && followUpWeeks <= 0) {
            setFollowUpValidationError('Follow up weeks is required');
            setMeetingDetailsTab('treatment');
            return;
        }

        // Clear any existing validation errors when saving
        setFollowUpValidationError(null);
        setIsPsySaving(true);
        try {
            console.log('[PSY Meeting] Saving all tab data to psy_meeting API');
            const response = await apiService.savePSYMeeting({
                token,
                id: selectedMeetingDetailsId,
                recommendations: editedRecommendations,
                summary_and_discussion: editedSummaryAndDiscussion,
                medications: psyMedications.map(med => ({
                    id: med.id || 0,
                    created_at: med.created_at,
                    updated_at: med.updated_at,
                    created_by: med.created_by,
                    patient_id: med.patient_id || 0,
                    meeting_id: med.meeting_id || selectedMeetingDetailsId,
                    medication_type_id: med.medication_type_id || 0,
                    name: med.name || '',
                    dosage: med.dosage || '0',
                    unit: med.unit || '',
                    per_day: med.per_day || '',
                    how_many_days: med.how_many_days || '',
                    amount_per_take: med.amount_per_take || 0,
                    amount: med.amount || 0,
                    amount_per: med.amount_per || 'Day',
                    duration: med.duration || 0,
                    number_of_packages: med.number_of_packages || 0,
                    likelihood_of_med_in_predictix: med.likelihood_of_med_in_predictix || '',
                    start_date: med.start_date || null,
                    comments: med.comments || '',
                })),
                diagnosis: psyDiagnosis.join(','),
                diagnosis_text: serializeDiagnosisText(),
                comments: editedSummaryAndDiscussion,
                need_followup_in_weeks: followUpWeeks,
                no_followups: needFollowUp === 'no',
                skip_casemanger_followup: skipCaseManager,
                clinic_assignment: clinicAssignment,
            });

            if (response.output_message === 'OK') {
                setNotification({ type: 'success', text: 'Data saved successfully' });
                setTimeout(() => setNotification(null), 3000);
                // Refresh meetings so the cached diagnosis/medications/notes
                // reflect the just-saved values.
                await refreshMeetings();
            } else {
                setNotification({
                    type: 'error',
                    text: response.output_message || 'Failed to save data'
                });
                setTimeout(() => setNotification(null), 5000);
            }
        } catch (error: any) {
            setNotification({ type: 'error', text: error.message || 'Failed to save data' });
            setTimeout(() => setNotification(null), 3000);
        } finally {
            setIsPsySaving(false);
        }
    };;

    const handlePSYFinish = async () => {
        // Validate follow-up settings
        if (needFollowUp === null) {
            setFollowUpValidationError('Please define Need follow Up Yes or No');
            setMeetingDetailsTab('treatment');
            return;
        }

        if (needFollowUp === 'yes' && followUpWeeks <= 0) {
            setFollowUpValidationError('Follow up weeks is required');
            setMeetingDetailsTab('treatment');
            return;
        }

        if (needFollowUp === 'yes' && followUpWeeks > 0 && !clinicAssignment) {
            setFollowUpValidationError('Clinic Assignment is required');
            setMeetingDetailsTab('treatment');
            return;
        }

        setFollowUpValidationError(null);

        if (!selectedMeetingDetailsId || !patientData?.id) {
            console.error('[PSY Meeting] Missing meeting or patient ID');
            return;
        }

        setIsPsyFinishing(true);
        try {
            // Step 1: Save all data using savePSYMeeting
            await apiService.savePSYMeeting({
                token,
                id: selectedMeetingDetailsId,
                recommendations: editedRecommendations,
                summary_and_discussion: editedSummaryAndDiscussion,
                medications: psyMedications.map(med => ({
                    id: med.id || 0,
                    created_at: med.created_at,
                    updated_at: med.updated_at,
                    created_by: med.created_by,
                    patient_id: med.patient_id || 0,
                    meeting_id: med.meeting_id || selectedMeetingDetailsId,
                    medication_type_id: med.medication_type_id || 0,
                    name: med.name || '',
                    dosage: med.dosage || '0',
                    unit: med.unit || '',
                    per_day: med.per_day || '',
                    how_many_days: med.how_many_days || '',
                    amount_per_take: med.amount_per_take || 0,
                    amount: med.amount || 0,
                    amount_per: med.amount_per || 'Day',
                    duration: med.duration || 0,
                    number_of_packages: med.number_of_packages || 0,
                    likelihood_of_med_in_predictix: med.likelihood_of_med_in_predictix || '',
                    start_date: med.start_date || null,
                    comments: med.comments || '',
                })),
                diagnosis: psyDiagnosis.join(','),
                diagnosis_text: serializeDiagnosisText(),
                comments: editedSummaryAndDiscussion,
                need_followup_in_weeks: followUpWeeks,
                no_followups: needFollowUp === 'no',
                skip_casemanger_followup: skipCaseManager,
                clinic_assignment: clinicAssignment,
            });

            // Refresh meetings so the cached diagnosis/medications/notes reflect
            // the just-saved values.
            await refreshMeetings();

            // Step 2: Generate summary
            console.log('[PSY Meeting] Generating summary for meeting:', selectedMeetingDetailsId);
            await apiService.generateSummary({
                token,
                meeting_id: selectedMeetingDetailsId,
            });

            // Step 3: Poll for summary readiness
            console.log('[PSY Meeting] Starting polling for summary status');
            setNotification({ type: 'success', text: 'Generating summary, please wait...' });

            // Clear any existing polling interval
            if (summaryPollingIntervalRef.current) {
                clearInterval(summaryPollingIntervalRef.current);
            }

            let pollAttempts = 0;
            const maxAttempts = 120; // 6 minutes max

            summaryPollingIntervalRef.current = setInterval(async () => {
                pollAttempts++;
                try {
                    const readyStatus = await apiService.checkIfSummaryReady({
                        token,
                        meeting_id: selectedMeetingDetailsId,
                    });

                    console.log('[PSY Meeting] Summary status check:', readyStatus);

                    if (readyStatus === true) {
                        // Summary is ready!
                        clearInterval(summaryPollingIntervalRef.current!);
                        summaryPollingIntervalRef.current = null;
                        setNotification(null);
                        setIsPsyFinishing(false);

                        // Open PSY summary modal
                        console.log('[PSY Meeting] Summary ready, opening modal');
                        setSelectedMeetingId(selectedMeetingDetailsId!);
                        setIsPSYSummaryModalOpen(true);
                    } else if (pollAttempts >= maxAttempts) {
                        // Timeout
                        clearInterval(summaryPollingIntervalRef.current!);
                        summaryPollingIntervalRef.current = null;
                        setNotification({ type: 'error', text: 'Summary generation timed out' });
                        setTimeout(() => setNotification(null), 3000);
                        setIsPsyFinishing(false);
                    }
                } catch (error: any) {
                    console.error('[PSY Meeting] Error checking summary status:', error);
                    // Continue polling even on error
                }
            }, 3000); // Poll every 3 seconds

        } catch (error: any) {
            console.error('[PSY Meeting] Error during save/generate:', error);
            setNotification({ type: 'error', text: error.message || 'Failed to process meeting' });
            setTimeout(() => setNotification(null), 3000);
            if (summaryPollingIntervalRef.current) {
                clearInterval(summaryPollingIntervalRef.current);
                summaryPollingIntervalRef.current = null;
            }
            setIsPsyFinishing(false);
        }
    };

    const handleCMFinish = async () => {
        if (!selectedMeetingDetailsId || !patientData?.id) {
            console.error('[CM Meeting] Missing meeting or patient ID');
            return;
        }

        setIsCmFinishing(true);
        try {
            // Step 1: Save comments
            console.log('[CM Meeting] Saving comments before finishing:', editedComments);
            await apiService.updateCMMeeting({
                token,
                id: selectedMeetingDetailsId,
                patient_id: patientData.id,
                comments: editedComments,
            });
            console.log('[CM Meeting] Comments saved successfully');

            // Step 2: Generate summary
            console.log('[CM Meeting] Generating summary for meeting:', selectedMeetingDetailsId);
            await apiService.generateSummary({
                token,
                meeting_id: selectedMeetingDetailsId,
            });

            // Step 3: Poll for summary readiness
            console.log('[CM Meeting] Starting polling for summary status');
            setNotification({ type: 'success', text: 'Generating summary, please wait...' });

            if (summaryPollingIntervalRef.current) {
                clearInterval(summaryPollingIntervalRef.current);
            }

            let pollAttempts = 0;
            const maxAttempts = 120; // 6 minutes max

            summaryPollingIntervalRef.current = setInterval(async () => {
                pollAttempts++;
                try {
                    const readyStatus = await apiService.checkIfSummaryReady({
                        token,
                        meeting_id: selectedMeetingDetailsId,
                    });

                    console.log('[CM Meeting] Summary status check:', readyStatus);

                    if (readyStatus === true) {
                        clearInterval(summaryPollingIntervalRef.current!);
                        summaryPollingIntervalRef.current = null;
                        setNotification(null);
                        setIsCmFinishing(false);

                        // Open CM summary modal
                        console.log('[CM Meeting] Summary ready, opening CM modal');
                        setSelectedMeetingId(selectedMeetingDetailsId!);
                        setIsCMSummaryModalOpen(true);
                    } else if (pollAttempts >= maxAttempts) {
                        clearInterval(summaryPollingIntervalRef.current!);
                        summaryPollingIntervalRef.current = null;
                        setNotification({ type: 'error', text: 'Summary generation timed out' });
                        setTimeout(() => setNotification(null), 3000);
                        setIsCmFinishing(false);
                    }
                } catch (error: any) {
                    console.error('[CM Meeting] Error checking summary status:', error);
                }
            }, 3000);

        } catch (error: any) {
            console.error('[CM Meeting] Error during save/generate:', error);
            setNotification({ type: 'error', text: error.message || 'Failed to process meeting' });
            setTimeout(() => setNotification(null), 3000);
            if (summaryPollingIntervalRef.current) {
                clearInterval(summaryPollingIntervalRef.current);
                summaryPollingIntervalRef.current = null;
            }
            setIsCmFinishing(false);
        }
    };

    const handleRequestDocuments = async (selectedDocuments: string[], notes?: string, selectedIds?: number[]) => {
        if (!patientData?.id || !selectedMeetingDetailsId) {
            console.error('[PatientPage] Missing patient or meeting ID');
            setNotification({
                type: 'error',
                text: 'Cannot send request: missing patient or meeting information'
            });
            return;
        }

        console.log('[PatientPage] Request documents:', selectedDocuments);
        console.log('[PatientPage] Notes:', notes);
        console.log('[PatientPage] Files IDs:', selectedIds);

        const payload = {
            token,
            patient_id: patientData.id,
            meeting_id: selectedMeetingDetailsId,
            user_id: Number(user.id),
            files_content: selectedDocuments,
            files_ids: selectedIds || [],
            files_message: notes || ''
        };

        console.log('[PatientPage] Full payload:', payload);
        console.log('[PatientPage] Payload as JSON string:', JSON.stringify(payload, null, 2));

        try {
            await apiService.uploadFilesRequest(payload);

            const refreshed = await apiService.getPatientPage({
                token,
                patient_id: patientId,
            });
            setPatientData(refreshed);

            setNotification({
                type: 'success',
                text: `Document request sent: ${selectedDocuments.length} document(s)`
            });
            setTimeout(() => setNotification(null), 3000);
        } catch (error: any) {
            console.error('[PatientPage] Failed to send document request:', error);
            setNotification({
                type: 'error',
                text: error.response?.data?.message || 'Failed to send document request'
            });
            setTimeout(() => setNotification(null), 3000);
        }
    };

    const ResizableHeader = ({ column, label }: { column: string; label: string }) => {
        const columnRef = React.useRef<HTMLTableCellElement>(null);
        const width = columnWidths[column];

        return (
            <th
                ref={columnRef}
                className="relative px-4 py-1 text-left text-xs font-medium text-gray-700 select-none"
                style={width ? { width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` } : {}}
            >
                {label}
                {/* Resize handle */}
                <div
                    className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-400 bg-gray-300"
                    onMouseDown={(e) => handleResizeStart(e, column, columnRef.current?.offsetWidth || 150)}
                    onClick={(e) => e.stopPropagation()}
                />
            </th>
        );
    };

    const renderContent = () => {
        if (isLoading) {
            return (
                <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="text-gray-600 mt-4">Loading patient information...</p>
                    </div>
                </div>
            );
        }

        if (error) {
            return (
                <div className="text-center py-12">
                    <p className="text-red-600 mb-4">{error}</p>
                    <CustomButton
                        variant="secondary"
                        size="sm"
                        onClick={() => onNavigate?.("patients")}
                    >
                        Back to Patients
                    </CustomButton>
                </div>
            );
        }

        if (!patientData) {
            return (
                <div className="text-center py-12">
                    <p className="text-gray-600">No patient data found</p>
                </div>
            );
        }

        return (
            <div className="flex flex-row gap-2 relative h-full w-full" ref={mainContainerRef}>
                {/* History Details Panel - Floating Over Left Pane */}
                {activeTab === 'patient-history' && selectedPatientHistoryItem && isPatientHistoryDetailsModalOpen && (
                    <div
                        className="fixed w-72 h-fit bg-white rounded-lg border border-gray-200 shadow-2xl flex flex-col overflow-hidden z-50"
                        style={{ left: `${historyDetailsPosition.x}px`, top: `${historyDetailsPosition.y}px` }}
                    >
                        {/* Header - Draggable */}
                        <div
                            className="flex items-center justify-between px-2 py-1.5 border-b border-gray-200 bg-gray-50 cursor-grab active:cursor-grabbing"
                            onMouseDown={handleHistoryDetailsHeaderMouseDown}
                        >
                            <h3 className="text-xs font-semibold text-gray-700">Details</h3>
                            <button
                                onClick={() => setIsPatientHistoryDetailsModalOpen(false)}
                                className="w-4 h-4 flex items-center justify-center text-gray-500 hover:text-gray-700 text-xs hover:bg-gray-200 rounded"
                            >
                                ✕
                            </button>
                        </div>

                        {/* Content */}
                        <div className="overflow-y-auto p-3 space-y-3 max-h-[80vh]">
                            {/* Notes Section */}
                            <div>
                                <label className="text-xs font-semibold text-gray-600 block mb-1">Notes</label>
                                <div
                                    className="bg-gray-50 border border-gray-200 rounded p-2 text-sm text-gray-700 whitespace-pre-wrap break-words"
                                    dir={getTextDirection(selectedPatientHistoryItem.notes)}
                                >
                                    {selectedPatientHistoryItem.notes || '-'}
                                </div>
                            </div>

                            {/* Meeting Information */}
                            {selectedPatientHistoryItem.meeting_id !== 0 && (
                                <div>
                                    <label className="text-xs font-semibold text-gray-600 block mb-1">Meeting</label>
                                    <div className="bg-gray-50 border border-gray-200 rounded p-2 text-xs space-y-1">
                                        {(() => {
                                            const meeting = meetings.find(m => m.id === selectedPatientHistoryItem.meeting_id);
                                            if (!meeting) {
                                                return <span className="text-gray-700">-</span>;
                                            }

                                            const meetingDate = new Date(meeting.date);
                                            const day = String(meetingDate.getDate()).padStart(2, '0');
                                            const month = String(meetingDate.getMonth() + 1).padStart(2, '0');
                                            const year = meetingDate.getFullYear();
                                            const formattedDate = `${day}/${month}/${year}`;

                                            return (
                                                <>
                                                    <div>
                                                        <span className="font-semibold text-gray-600">Date: </span>
                                                        <span className="text-gray-700">{formattedDate}</span>
                                                    </div>
                                                    <div>
                                                        <span className="font-semibold text-gray-600">Type: </span>
                                                        <span className="text-gray-700">{meeting.meetingtype_id ? (meetingTypes.find(mt => mt.id === meeting.meetingtype_id)?.name || '-') : '-'}</span>
                                                    </div>
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* LEFT SIDE - Patient Overview / Meeting Summary */}
                <div className={`bg-white flex flex-col h-full overflow-visible relative ${isRightPanelCollapsed ? 'flex-[0_0_100%]' : ''
                    }`} style={!isRightPanelCollapsed ? { flex: `0 0 max(${leftPaneWidth}%, 70px)`, minWidth: '70px' } : { flex: `0 0 max(97%, 30px)`, minWidth: '30px' }}>
                    <div className="px-0 py-0 border-b border-gray-200 flex items-center justify-between">
                        {selectedMeetingDetailsId ? (() => {
                            const selectedMeeting = meetings.find(m => m.id === selectedMeetingDetailsId);
                            if (selectedMeeting) {
                                const meetingTypeId = selectedMeeting.meetingtype_id;
                                const meetingTypeName = getMeetingTypeName(selectedMeeting, meetingTypes);

                                return (
                                    <div className="flex items-center justify-between w-full gap-2 py-1">
                                        {(meetingTypeId === 1 || meetingTypeId === 4) && (
                                            <div className="flex items-center gap-0">
                                                {/* Tabs for Summary and Agenda */}
                                                <button
                                                    onClick={() => {
                                                        setMeetingDetailsTab('summary');
                                                        setIsDropdownOpen(false);
                                                    }}
                                                    className={`px-1 py-1 text-xs font-medium border-b-2 ${meetingDetailsTab === 'summary' ? 'text-blue-700 border-blue-700' : 'text-gray-600 border-transparent'}`}
                                                >
                                                    Summary
                                                </button>
                                                {meetingTypeId === 1 && (
                                                    <button
                                                        onClick={() => {
                                                            setMeetingDetailsTab('agenda');
                                                            setSelectedSectionIndex(0);
                                                            setSelectedSubsectionIndex(0);
                                                            setIsDropdownOpen(false);
                                                        }}
                                                        className={`px-1 py-1 text-xs font-medium border-b-2 ml-2 ${meetingDetailsTab === 'agenda' ? 'text-blue-700 border-blue-700' : 'text-gray-600 border-transparent'}`}
                                                    >
                                                        Agenda
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => {
                                                        setMeetingDetailsTab('app');
                                                        setIsDropdownOpen(false);
                                                    }}
                                                    className={`px-1 py-1 text-xs font-medium border-b-2 ml-2 ${meetingDetailsTab === 'app' ? 'text-blue-700 border-blue-700' : 'text-gray-600 border-transparent'}`}
                                                >
                                                    App
                                                </button>
                                            </div>
                                        )}

                                        <h2 className="text-sm font-semibold text-gray-900 flex-shrink-0" style={{ marginLeft: '100px' }}>{meetingTypeName}</h2>
                                        {selectedMeeting.warning && (
                                            <div className="flex-1 text-center">
                                                <span className="text-xs font-medium text-red-600">{selectedMeeting.warning}</span>
                                            </div>
                                        )}
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            {meetingDetailsTab === 'summary' && (
                                                <div className="relative">
                                                    <button
                                                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                                        className="px-2 py-1 border border-gray-300 rounded bg-white text-gray-700 text-xs font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[120px] flex items-center gap-1"
                                                    >
                                                        <span className="flex-1 text-left truncate">{selectedSummaryId ? patientSummaries.find(s => s.id === selectedSummaryId)?.name || 'Summary' : 'Summary'}</span>
                                                        <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 10l5 5m0 0l5-5m-5 5V3" />
                                                        </svg>
                                                    </button>
                                                    {isDropdownOpen && (
                                                        <div ref={dropdownRef} className="absolute top-full right-0 mt-2 w-96 bg-white border border-gray-300 rounded-lg shadow-lg z-50">
                                                            <div className="max-h-64 overflow-y-auto">
                                                                {patientSummaries.length === 0 ? (
                                                                    <div className="px-4 py-3 text-gray-500 text-sm italic">No summaries available</div>
                                                                ) : (
                                                                    patientSummaries.map((summary) => (
                                                                        <button
                                                                            data-ai-id="summary-history-option"
                                                                            key={summary.id}
                                                                            onClick={() => {
                                                                                setSelectedSummaryId(summary.id);
                                                                                setSelectedSummaryText(summary.summary);
                                                                                setIsDropdownOpen(false);
                                                                            }}
                                                                            className={`w-full px-4 py-3 text-left text-sm border-b border-gray-100 last:border-b-0 transition-colors whitespace-nowrap overflow-hidden text-ellipsis hover:bg-gray-100 text-gray-700 ${selectedSummaryId === summary.id ? 'font-medium' : ''
                                                                                }`}
                                                                        >
                                                                            {summary.name}
                                                                        </button>
                                                                    ))
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            <button
                                                onClick={() => {
                                                    setSelectedMeetingDetailsId(null);
                                                    // Remove meeting_id from URL
                                                    const params = new URLSearchParams(window.location.search);
                                                    params.delete('meeting_id');
                                                    const newSearch = params.toString();
                                                    window.history.pushState({}, '', newSearch ? `${window.location.pathname}?${newSearch}` : window.location.pathname);
                                                }}
                                                className="text-gray-500 hover:text-gray-700 text-sm mr-1.5"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    </div>
                                );
                            }
                            return <h2 className="text-sm font-semibold text-gray-900 ml-4">Patient Overview</h2>;
                        })() : (
                            <div className="flex items-center justify-between w-full">
                                <h2 className="text-sm font-semibold text-gray-900 ml-4">Patient Overview</h2>
                                <div className="relative">
                                    <button
                                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                        className="px-2 py-1 border border-gray-300 rounded bg-white text-gray-700 text-xs font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[120px] flex items-center gap-1"
                                    >
                                        <span className="flex-1 text-left truncate">{selectedSummaryId ? patientSummaries.find(s => s.id === selectedSummaryId)?.name || 'Summary' : 'Summary'}</span>
                                        <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 10l5 5m0 0l5-5m-5 5V3" />
                                        </svg>
                                    </button>
                                    {isDropdownOpen && (
                                        <div ref={dropdownRef} className="absolute top-full right-0 mt-2 w-96 bg-white border border-gray-300 rounded-lg shadow-lg z-50">
                                            <div className="max-h-64 overflow-y-auto">
                                                {patientSummaries.length === 0 ? (
                                                    <div className="px-4 py-3 text-gray-500 text-sm italic">No summaries available</div>
                                                ) : (
                                                    patientSummaries.map((summary) => (
                                                        <button
                                                            data-ai-id="cm-summary-history-option"
                                                            key={summary.id}
                                                            onClick={() => {
                                                                setSelectedSummaryId(summary.id);
                                                                setSelectedSummaryText(summary.summary);
                                                                setIsDropdownOpen(false);
                                                            }}
                                                            className={`w-full px-4 py-3 text-left text-sm border-b border-gray-100 last:border-b-0 transition-colors whitespace-nowrap overflow-hidden text-ellipsis hover:bg-gray-100 text-gray-700 ${selectedSummaryId === summary.id ? 'font-medium' : ''
                                                                }`}
                                                        >
                                                            {summary.name}
                                                        </button>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="p-0 flex-1 flex flex-col overflow-hidden h-full">
                        {selectedMeetingDetailsId ? (() => {
                            const selectedMeeting = meetings.find(m => m.id === selectedMeetingDetailsId);
                            if (selectedMeeting) {
                                const meetingTypeId = selectedMeeting.meetingtype_id;
                                const isCMMeeting = meetingTypeId === 1 || meetingTypeId === 4;
                                const isPSYMeeting = meetingTypeId === 2 || meetingTypeId === 3;
                                const isCMIntakeLike = meetingTypeId === 1 || (meetingTypeId === 4 && selectedMeeting.re_inatke);

                                return (
                                    <div className="flex flex-col h-full">
                                        {isCMMeeting ? (
                                            isCmMeetingDataLoading ? (
                                                <p className="text-xs text-gray-500 italic">Loading meeting details...</p>
                                            ) : cmMeetingData ? (
                                                <div className="flex flex-col h-full" ref={containerRef}>
                                                    {meetingDetailsTab === 'app' ? (
                                                        <AppTab patientId={Number(patientId)} token={token} />
                                                    ) : meetingDetailsTab === 'summary' ? (
                                                        <>
                                                            <div className="flex flex-col h-full flex-1">
                                                                {/* Agenda/Summary Text Box */}
                                                                <textarea
                                                                    value={selectedSummaryText || ''}
                                                                    readOnly
                                                                    dir={textDirection}
                                                                    className="w-full px-3 py-2 border border-gray-300 text-gray-900 bg-white resize-none overflow-y-auto"
                                                                    style={{ height: `${summaryHeight}%`, fontSize: '13px' }}
                                                                    placeholder="No summary available"
                                                                />

                                                                {/* Draggable Divider */}
                                                                <div
                                                                    onMouseDown={() => setIsDraggingDivider(true)}
                                                                    className="w-full h-1 bg-transparent hover:bg-gray-300 cursor-row-resize transition-colors"
                                                                    title="Drag to resize"
                                                                />

                                                                {/* Comments Text Box */}
                                                                <textarea
                                                                    value={editedComments}
                                                                    onChange={(e) => setEditedComments(e.target.value)}
                                                                    dir={textDirection}
                                                                    className="w-full px-3 py-2 border border-gray-300 text-xs text-gray-700 bg-white resize-none overflow-y-auto"
                                                                    style={{ height: `${100 - summaryHeight}%` }}
                                                                    placeholder="No comments available"
                                                                />
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            {/* Agenda View with Comments */}
                                                            <div className="flex flex-col h-full flex-1">
                                                                {/* Agenda Section */}
                                                                <div className="flex flex-col overflow-hidden" style={{ height: `${summaryHeight}%` }} dir={textDirection}>
                                                                    {cmMeetingData.agenda_object?.sections ? (
                                                                        <div className="flex h-full gap-4 p-4">
                                                                            {/* Right: Sections */}
                                                                            <div className={`flex flex-col gap-2 overflow-y-auto w-56 border-r border-gray-200 ${textDirection === 'ltr' ? 'pl-4' : 'pr-4'}`} dir={textDirection}>
                                                                                {cmMeetingData.agenda_object.sections.map((section: any, sectionIdx: number) => (
                                                                                    <button
                                                                                        data-ai-id="history-section-tab"
                                                                                        key={sectionIdx}
                                                                                        onClick={() => {
                                                                                            setSelectedSectionIndex(sectionIdx);
                                                                                            setSelectedSubsectionIndex(0);
                                                                                        }}
                                                                                        className={`w-full px-3 py-2 text-xs rounded transition-colors ${textDirection === 'ltr' ? 'text-left' : 'text-right'} whitespace-normal break-words ${selectedSectionIndex === sectionIdx
                                                                                            ? 'bg-blue-100 text-blue-700 border border-blue-300 font-medium'
                                                                                            : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                                                                                            }`}
                                                                                    >
                                                                                        {section.sectionName}
                                                                                    </button>
                                                                                ))}
                                                                            </div>

                                                                            {/* Middle: Subsections */}
                                                                            <div className="flex flex-col gap-2 overflow-y-auto w-56" dir={textDirection}>
                                                                                {selectedSectionIndex < cmMeetingData.agenda_object.sections.length && cmMeetingData.agenda_object.sections[selectedSectionIndex].subsections?.map((subsection: any, subsectionIdx: number) => (
                                                                                    <button
                                                                                        data-ai-id="history-subsection-tab"
                                                                                        key={subsectionIdx}
                                                                                        onClick={() => setSelectedSubsectionIndex(subsectionIdx)}
                                                                                        className={`w-full px-3 py-2 text-xs rounded transition-colors ${textDirection === 'ltr' ? 'text-left' : 'text-right'} whitespace-normal break-words ${selectedSubsectionIndex === subsectionIdx
                                                                                            ? 'bg-green-100 text-green-700 border border-green-300 font-medium'
                                                                                            : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                                                                                            }`}
                                                                                    >
                                                                                        {subsection.subsectionName}
                                                                                    </button>
                                                                                ))}
                                                                            </div>

                                                                            {/* Left: Questions */}
                                                                            <div className="flex-1 flex flex-col border-l border-gray-200 pl-4 overflow-hidden" dir={textDirection}>
                                                                                <h3 className={`text-xs font-semibold text-gray-700 mb-2 ${textDirection === 'ltr' ? 'text-left' : 'text-right'}`}>Questions</h3>
                                                                                <div className="flex-1 overflow-y-auto">
                                                                                    {selectedSectionIndex < cmMeetingData.agenda_object.sections.length && selectedSubsectionIndex < cmMeetingData.agenda_object.sections[selectedSectionIndex].subsections.length ? (
                                                                                        <ul className={`space-y-2 list-disc ${textDirection === 'ltr' ? 'pl-5' : 'pr-5'}`}>
                                                                                            {cmMeetingData.agenda_object.sections[selectedSectionIndex].subsections[selectedSubsectionIndex].questions?.map((question: string, idx: number) => (
                                                                                                <li key={idx} className={`text-xs text-gray-700 leading-tight ${textDirection === 'ltr' ? 'text-left' : 'text-right'}`}>
                                                                                                    {question}
                                                                                                </li>
                                                                                            ))}
                                                                                        </ul>
                                                                                    ) : (
                                                                                        <p className={`text-xs text-gray-500 italic ${textDirection === 'ltr' ? 'text-left' : 'text-right'}`}>No questions available</p>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    ) : (
                                                                        <p className="text-xs text-gray-500 italic p-4 text-right">אגנדה</p>
                                                                    )}
                                                                </div>

                                                                {/* Draggable Divider */}
                                                                <div
                                                                    onMouseDown={() => setIsDraggingDivider(true)}
                                                                    className="w-full h-1 bg-transparent hover:bg-gray-300 cursor-row-resize transition-colors"
                                                                    title="Drag to resize"
                                                                />

                                                                {/* Comments Text Box */}
                                                                <textarea
                                                                    value={editedComments}
                                                                    onChange={(e) => setEditedComments(e.target.value)}
                                                                    dir={textDirection}
                                                                    className="w-full px-3 py-2 border border-gray-300 text-xs text-gray-700 bg-white resize-none overflow-y-auto"
                                                                    style={{ height: `${100 - summaryHeight}%` }}
                                                                    placeholder="No comments available"
                                                                />
                                                            </div>
                                                        </>
                                                    )}

                                                    {/* Bottom Bar - hidden on App tab to match PSY behavior */}
                                                    {meetingDetailsTab !== 'app' && (
                                                    <div className="flex items-center gap-4 mt-1 pt-2 pb-2 border-t border-gray-300">
                                                        {/* Left: Link and No Show Icons */}
                                                        <div className="flex items-center gap-2 flex-shrink-0">
                                                            <button
                                                                    onClick={() => setMeetingForCall({
                                                                        id: selectedMeetingDetailsId!,
                                                                        patient_id: Number(patientId),
                                                                        patient_name: patientData?.patient_name || `${patientData?.first_name || ""} ${patientData?.last_name || ""}`.trim(),
                                                                        patient_phone: patientData?.phone,
                                                                    })}
                                                                    className="p-2 text-green-600 hover:text-green-700 transition-colors"
                                                                    title="Call Patient"
                                                                >
                                                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                                                    </svg>
                                                                </button>
                                                            <button
                                                                onClick={async () => {
                                                                    if (selectedMeeting?.team_url) {
                                                                        window.open(selectedMeeting.team_url, '_blank');
                                                                    }
                                                                    if (shouldStartRecallBot(selectedMeeting) && selectedMeetingDetailsId) {
                                                                        try {
                                                                            const apiUrl = localStorage.getItem('apiUrl') || process.env.REACT_APP_API_URL || 'https://dev-backend.taliaz-cm.com';
                                                                            const url = `${apiUrl}/start_meeting?token=${token}&meeting_id=${selectedMeetingDetailsId}`;
                                                                            await fetch(url, { method: 'GET' });
                                                                            console.log(`[RT Meeting] Called start_meeting for meeting ${selectedMeetingDetailsId}`);
                                                                        } catch (error) {
                                                                            console.error('[RT Meeting] Failed to call start_meeting:', error);
                                                                        }
                                                                    }
                                                                }}
                                                                className="p-2 text-gray-600 hover:text-blue-600 transition-colors"
                                                                title="Team Link"
                                                            >
                                                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.658 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                                                </svg>
                                                            </button>
                                                            {selectedMeeting?.status !== 'noshow' && (
                                                                <button
                                                                    onClick={() => {
                                                                        setIsNoShowConfirmationOpen(true);
                                                                    }}
                                                                    className="p-2 text-red-600 hover:text-red-700 transition-colors text-xl"
                                                                    title="No Show"
                                                                >
                                                                    ⛔
                                                                </button>
                                                            )}
                                                            <button
                                                                onClick={() => setIsFinishTreatmentModalOpen(true)}
                                                                className="p-2 text-red-600 hover:text-red-700 transition-colors"
                                                                title="Finish Treatment"
                                                            >
                                                                <svg className="w-6 h-6" fill="#e23232" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                                    <path d="M4,1A1,1,0,0,0,3,2V22a1,1,0,0,0,2,0V17H20a1,1,0,0,0,1-1V4a1,1,0,0,0-1-1H5V2A1,1,0,0,0,4,1ZM7,15H5V13H7Zm0-4H5V9H7ZM17,5h2V7H17Zm0,4h2v2H17Zm0,4h2v2H17ZM13,5h2V7H13Zm0,4h2v2H13Zm0,4h2v2H13ZM9,5h2V7H9ZM9,9h2v2H9Zm0,4h2v2H9ZM7,5V7H5V5Z" />
                                                                </svg>
                                                            </button>
                                                            <button
                                                                onClick={async () => {
                                                                    const token = localStorage.getItem("token") || "";
                                                                    try {
                                                                        setNotification({ type: 'success', text: 'Sending team link...' });
                                                                        await apiService.sendMeetingLink(token, selectedMeetingDetailsId);
                                                                        setNotification({ type: 'success', text: 'Team link sent successfully!' });
                                                                        setTimeout(() => setNotification(null), 3000);
                                                                    } catch (error: any) {
                                                                        console.error("Failed to send team link:", error);
                                                                        setNotification({ type: 'error', text: error.message || 'Failed to send team link' });
                                                                        setTimeout(() => setNotification(null), 3000);
                                                                    }
                                                                }}
                                                                className="p-2 text-blue-600 hover:text-blue-700 transition-colors"
                                                                title="Send Meeting Link"
                                                            >
                                                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                                    <rect x="3" y="5" width="18" height="14" rx="2" ry="2" strokeWidth="2" />
                                                                    <path d="M3 7l9 6 9-6" strokeWidth="2" />
                                                                    <circle cx="17" cy="16" r="3" fill="currentColor" stroke="white" strokeWidth="1.5" />
                                                                    <path d="M15.5 16h3M17 14.5v3" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                                                                </svg>
                                                            </button>
                                                            <button
                                                                onClick={() => setIsRequestDocumentsModalOpen(true)}
                                                                className="p-2 text-orange-500 hover:text-orange-600 transition-colors"
                                                                title="Request Documents"
                                                            >
                                                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                                </svg>
                                                            </button>
                                                        </div>

                                                        {/* Flex spacer to push buttons to center */}
                                                        <div className="flex-1"></div>

                                                        {/* Middle: Save and Finish Buttons */}
                                                        <div className="flex items-center gap-2 flex-shrink-0">
                                                            <button
                                                                onClick={async () => {
                                                                    if (!selectedMeetingDetailsId || !patientData?.id) {
                                                                        console.error('[Meeting] Missing meeting or patient ID');
                                                                        return;
                                                                    }
                                                                    try {
                                                                        console.log('[Meeting] Saving comments:', editedComments);
                                                                        const response = await apiService.updateCMMeeting({
                                                                            token,
                                                                            id: selectedMeetingDetailsId,
                                                                            patient_id: patientData.id,
                                                                            comments: editedComments,
                                                                        });
                                                                        console.log('[Meeting] Save response:', response.output_message);

                                                                        if (response.output_message === 'OK') {
                                                                            console.log('[Meeting] Comments saved successfully');
                                                                            setNotification({ type: 'success', text: 'Comments saved successfully' });
                                                                            setTimeout(() => setNotification(null), 3000);
                                                                        } else {
                                                                            console.error('[Meeting] Save error:', response.output_message);
                                                                            setNotification({ type: 'error', text: response.output_message || 'Failed to save comments' });
                                                                            setTimeout(() => setNotification(null), 3000);
                                                                        }
                                                                    } catch (error: any) {
                                                                        console.error('[Meeting] Failed to save comments:', error);
                                                                        setNotification({ type: 'error', text: error.message || 'Failed to save comments' });
                                                                        setTimeout(() => setNotification(null), 3000);
                                                                    }
                                                                }}
                                                                className="px-4 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                                                            >
                                                                Save
                                                            </button>
                                                            {selectedMeeting?.status !== 'done' && (
                                                                isCMIntakeLike ? (
                                                                    <button
                                                                        onClick={handleCMFinish}
                                                                        disabled={isCmFinishing}
                                                                        className="px-4 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                                    >
                                                                        {isCmFinishing ? 'Summarizing...' : 'Finish & Summarize'}
                                                                    </button>
                                                                ) : (
                                                                    <button
                                                                        onClick={async () => {
                                                                            if (!selectedMeetingDetailsId || !patientData?.id) {
                                                                                console.error('[Meeting] Missing meeting or patient ID');
                                                                                return;
                                                                            }
                                                                            try {
                                                                                // First, save comments
                                                                                console.log('[Meeting] Saving comments before finishing:', editedComments);
                                                                                await apiService.updateCMMeeting({
                                                                                    token,
                                                                                    id: selectedMeetingDetailsId,
                                                                                    patient_id: patientData.id,
                                                                                    comments: editedComments,
                                                                                });
                                                                                console.log('[Meeting] Comments saved successfully');

                                                                                // Then, finish the meeting
                                                                                console.log('[Meeting] Finishing meeting:', selectedMeetingDetailsId);
                                                                                const response = await apiService.finishMeeting({
                                                                                    token,
                                                                                    meeting_id: selectedMeetingDetailsId,
                                                                                    no_transcript: false,
                                                                                    skip_warnings: false,
                                                                                });
                                                                                console.log('[Meeting] Meeting finish response:', response.status);

                                                                                // Handle different response statuses
                                                                                if (response.status === 'OK') {
                                                                                    console.log('[Meeting] Meeting finished successfully');
                                                                                    setNotification({ type: 'success', text: 'Meeting finished successfully' });
                                                                                    setTimeout(() => {
                                                                                        setNotification(null);
                                                                                        onNavigate?.(selectedMeeting?.ad_hoc ? 'adhoc-meetings' : 'calendar');
                                                                                    }, 3000);
                                                                                } else if (response.status === 'Error') {
                                                                                    console.error('[Meeting] Error finishing meeting:', response.text);
                                                                                    setFinishErrorMessage(response.text);
                                                                                    setIsFinishErrorModalOpen(true);
                                                                                } else if (response.status === 'Warn') {
                                                                                    console.warn('[Meeting] Warnings received:', response.text);
                                                                                    const warnings = response.text.split(',').map(w => w.trim()).filter(w => w);
                                                                                    setFinishWarnings(warnings);
                                                                                    setFinishWarningMeetingId(selectedMeetingDetailsId);
                                                                                    setIsFinishWarningModalOpen(true);
                                                                                }
                                                                            } catch (error: any) {
                                                                                console.error('[Meeting] Failed to finish meeting:', error);
                                                                                setFinishErrorMessage(error.message || 'Failed to finish meeting');
                                                                                setIsFinishErrorModalOpen(true);
                                                                            }
                                                                        }}
                                                                        className="px-4 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                                                                    >
                                                                        Finish
                                                                    </button>
                                                                )
                                                            )}
                                                            {selectedMeeting?.status === 'done' && (
                                                                <button
                                                                    onClick={() => {
                                                                        setSelectedMeetingId(selectedMeetingDetailsId);
                                                                        setIsCMSummaryModalOpen(true);
                                                                    }}
                                                                    className="px-4 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                                                                >
                                                                    View Summary
                                                                </button>
                                                            )}
                                                        </div>

                                                        {/* Flex spacer to separate from address */}
                                                        <div className="flex-1"></div>

                                                        {/* Right: Meeting Address */}
                                                        <div className={`text-xs flex-shrink-0 mr-2 truncate ${selectedMeeting?.Severe ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                                                            {selectedMeeting?.meeting_address || '-'}
                                                        </div>
                                                    </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <p className="text-xs text-gray-500 italic">No meeting details available</p>
                                            )
                                        ) : isPSYMeeting ? (
                                            // PSY MEETING PANEL
                                            isPsyMeetingDataLoading ? (
                                                <p className="text-xs text-gray-500 italic">Loading meeting details...</p>
                                            ) : psyMeetingData ? (
                                                <div className="flex flex-col h-full">
                                                    {/* Tabs */}
                                                    <div className="flex items-center gap-0 py-1 px-2 border-b border-gray-200">
                                                        <button
                                                            onClick={() => setMeetingDetailsTab('summary')}
                                                            className={`px-1 py-1 text-xs font-medium border-b-2 ${meetingDetailsTab === 'summary'
                                                                ? 'text-blue-700 border-blue-700'
                                                                : 'text-gray-600 border-transparent'
                                                                }`}
                                                        >
                                                            Summary
                                                        </button>
                                                        <button
                                                            onClick={() => setMeetingDetailsTab('agenda')}
                                                            className={`px-1 py-1 text-xs font-medium border-b-2 ml-2 ${meetingDetailsTab === 'agenda'
                                                                ? 'text-blue-700 border-blue-700'
                                                                : 'text-gray-600 border-transparent'
                                                                }`}
                                                        >
                                                            Agenda
                                                        </button>
                                                        <button
                                                            onClick={() => setMeetingDetailsTab('treatment')}
                                                            className={`px-1 py-1 text-xs font-medium border-b-2 ml-2 ${meetingDetailsTab === 'treatment'
                                                                ? 'text-blue-700 border-blue-700'
                                                                : 'text-gray-600 border-transparent'
                                                                }`}
                                                        >
                                                            Treatment
                                                        </button>
                                                        <button
                                                            onClick={() => setMeetingDetailsTab('app')}
                                                            className={`px-1 py-1 text-xs font-medium border-b-2 ml-2 ${meetingDetailsTab === 'app'
                                                                ? 'text-blue-700 border-blue-700'
                                                                : 'text-gray-600 border-transparent'
                                                                }`}
                                                        >
                                                            App
                                                        </button>
                                                    </div>

                                                    {/* Tab Content */}
                                                    {meetingDetailsTab === 'app' ? (
                                                        <AppTab patientId={Number(patientId)} token={token} />
                                                    ) : meetingDetailsTab === 'summary' ? (
                                                        <div className="flex flex-col h-full flex-1 overflow-hidden" ref={containerRef}>
                                                            {/* Top: Read-only summary */}
                                                            <textarea
                                                                value={selectedSummaryText || ''}
                                                                readOnly
                                                                dir={textDirection}
                                                                className="w-full px-3 py-2 border border-gray-300 text-gray-900 bg-white resize-none overflow-y-auto"
                                                                style={{ height: `${psySummaryHeight}%`, fontSize: '13px' }}
                                                                placeholder="No summary available"
                                                            />

                                                            {/* Draggable Divider */}
                                                            <div
                                                                onMouseDown={() => setIsPsyDraggingDivider(true)}
                                                                className="w-full h-1 bg-transparent hover:bg-gray-300 cursor-row-resize"
                                                            />

                                                            {/* Bottom: Two editable textareas side by side */}
                                                            <div className="flex flex-1 overflow-hidden">
                                                                {/* Left: summary_and_discussion */}
                                                                <textarea
                                                                    value={editedSummaryAndDiscussion}
                                                                    onChange={(e) => setEditedSummaryAndDiscussion(e.target.value)}
                                                                    dir={textDirection}
                                                                    className="flex-1 px-3 py-2 border-r border-gray-300 text-xs text-gray-700 bg-white resize-none overflow-y-auto"
                                                                    placeholder="Summary and Discussion"
                                                                    style={{ margin: 0, padding: '8px' }}
                                                                />

                                                                {/* Right: recommendations */}
                                                                <textarea
                                                                    value={editedRecommendations}
                                                                    onChange={(e) => setEditedRecommendations(e.target.value)}
                                                                    dir={textDirection}
                                                                    className="flex-1 px-3 py-2 text-xs text-gray-700 bg-white resize-none overflow-y-auto"
                                                                    placeholder="Recommendations"
                                                                    style={{ margin: 0, padding: '8px' }}
                                                                />
                                                            </div>

                                                            {/* Bottom Bar - Action Buttons */}
                                                            <MeetingActionBar
                                                                meetingTypeId={selectedMeeting?.meetingtype_id}
                                                                meeting={{
                                                                    id: selectedMeetingDetailsId,
                                                                    team_url: selectedMeeting?.team_url,
                                                                    status: selectedMeeting?.status,
                                                                    re_inatke: selectedMeeting?.re_inatke,
                                                                }}
                                                                onSave={handlePSYSave}
                                                                onFinish={handlePSYFinish}
                                                                onNoShow={() => setIsNoShowConfirmationOpen(true)}
                                                                onFinishTreatment={() => setIsFinishTreatmentModalOpen(true)}
                                                                onSendMeetingLink={async () => {
                                                                    const token = localStorage.getItem("token") || "";
                                                                    try {
                                                                        setNotification({ type: 'success', text: 'Sending team link...' });
                                                                        await apiService.sendMeetingLink(token, selectedMeetingDetailsId);
                                                                        setNotification({ type: 'success', text: 'Team link sent successfully!' });
                                                                        setTimeout(() => setNotification(null), 3000);
                                                                    } catch (error: any) {
                                                                        console.error("Failed to send team link:", error);
                                                                        setNotification({ type: 'error', text: error.message || 'Failed to send team link' });
                                                                        setTimeout(() => setNotification(null), 3000);
                                                                    }
                                                                }}
                                                                onRequestDocuments={() => setIsRequestDocumentsModalOpen(true)}
                                                                onCall={() => setMeetingForCall({ id: selectedMeetingDetailsId!, patient_id: Number(patientId), patient_name: patientData?.patient_name, patient_phone: patientData?.phone })}                                                                isSaving={isPsySaving}
                                                                isFinishing={isPsyFinishing}
                                                                additionalButtons={
                                                                    selectedMeeting?.status === 'done' ? (
                                                                        <button
                                                                            onClick={() => {
                                                                                setSelectedMeetingId(selectedMeetingDetailsId);
                                                                                setIsPSYSummaryModalOpen(true);
                                                                            }}
                                                                            className="px-4 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                                                                        >
                                                                            View Summary
                                                                        </button>
                                                                    ) : null
                                                                }
                                                                rightContent={
                                                                    <div className={`text-xs flex-shrink-0 mr-2 truncate ${selectedMeeting?.Severe ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                                                                        {selectedMeeting?.meeting_address || '-'}
                                                                    </div>
                                                                }
                                                            />
                                                        </div>
                                                    ) : meetingDetailsTab === 'agenda' ? (
                                                        <div className="flex flex-col h-full flex-1 overflow-hidden">
                                                            {isPsyAgendaLoading ? (
                                                                <p className="text-xs text-gray-500 italic p-4">Loading agenda...</p>
                                                            ) : (
                                                                <textarea
                                                                    value={psyMeetingAgenda}
                                                                    readOnly
                                                                    dir={textDirection}
                                                                    className="flex-1 px-3 py-2 border border-gray-300 text-xs text-gray-700 bg-white resize-none overflow-y-auto"
                                                                    placeholder="No agenda available"
                                                                    style={{ margin: 0, padding: '8px' }}
                                                                />
                                                            )}

                                                            {/* Bottom Bar - Action Buttons */}
                                                            <MeetingActionBar
                                                                meetingTypeId={selectedMeeting?.meetingtype_id}
                                                                meeting={{
                                                                    id: selectedMeetingDetailsId,
                                                                    team_url: selectedMeeting?.team_url,
                                                                    status: selectedMeeting?.status,
                                                                    re_inatke: selectedMeeting?.re_inatke,
                                                                }}
                                                                onSave={handlePSYSave}
                                                                onFinish={handlePSYFinish}
                                                                onNoShow={() => setIsNoShowConfirmationOpen(true)}
                                                                onFinishTreatment={() => setIsFinishTreatmentModalOpen(true)}
                                                                onSendMeetingLink={async () => {
                                                                    const token = localStorage.getItem("token") || "";
                                                                    try {
                                                                        setNotification({ type: 'success', text: 'Sending team link...' });
                                                                        await apiService.sendMeetingLink(token, selectedMeetingDetailsId);
                                                                        setNotification({ type: 'success', text: 'Team link sent successfully!' });
                                                                        setTimeout(() => setNotification(null), 3000);
                                                                    } catch (error: any) {
                                                                        console.error("Failed to send team link:", error);
                                                                        setNotification({ type: 'error', text: error.message || 'Failed to send team link' });
                                                                        setTimeout(() => setNotification(null), 3000);
                                                                    }
                                                                }}
                                                                onRequestDocuments={() => setIsRequestDocumentsModalOpen(true)}
                                                                onCall={() => setMeetingForCall({ id: selectedMeetingDetailsId!, patient_id: Number(patientId), patient_name: patientData?.patient_name, patient_phone: patientData?.phone })}                                                                isSaving={isPsySaving}
                                                                isFinishing={isPsyFinishing}
                                                                additionalButtons={
                                                                    selectedMeeting?.status === 'done' ? (
                                                                        <button
                                                                            onClick={() => {
                                                                                setSelectedMeetingId(selectedMeetingDetailsId);
                                                                                setIsPSYSummaryModalOpen(true);
                                                                            }}
                                                                            className="px-4 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                                                                        >
                                                                            View Summary
                                                                        </button>
                                                                    ) : null
                                                                }
                                                                rightContent={
                                                                    <div className={`text-xs flex-shrink-0 mr-2 truncate ${selectedMeeting?.Severe ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                                                                        {selectedMeeting?.meeting_address || '-'}
                                                                    </div>
                                                                }
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div className="flex flex-col h-full flex-1 overflow-hidden">
                                                            {/* Content Area - Scrollable */}
                                                            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                                                                {/* Diagnosis Section */}
                                                                <div>
                                                                    <label className="block text-xs font-medium text-gray-700 mb-2" dir={textDirection}>
                                                                        Diagnosis
                                                                    </label>

                                                                    {/* Selected diagnoses - one per row, with optional per-diagnosis note input */}
                                                                    {psyDiagnosis.length > 0 && (
                                                                        <div className="flex flex-col gap-2 mb-2">
                                                                            {psyDiagnosis.map((code) => {
                                                                                const diagnosisItem = diagnosisCodes?.find(d => d.code === code);
                                                                                const note = psyDiagnosisTextMap[code] || '';
                                                                                const isEditing = editingNoteCode === code;
                                                                                return (
                                                                                    <div key={code} className="flex flex-col gap-1">
                                                                                        <div className="flex items-center gap-2">
                                                                                            {/* Chip: code + name, with optional connected note segment */}
                                                                                            <span className="inline-flex items-stretch text-xs rounded-lg overflow-hidden border border-[#B4D9D4] flex-shrink-0">
                                                                                                <button
                                                                                                    type="button"
                                                                                                    onClick={() => handleStartEditNote(code)}
                                                                                                    title="Click to add or edit a note"
                                                                                                    className="px-2 py-1 bg-[#B5D9D4] text-gray-900 text-left hover:bg-[#a6cfc9] transition-colors"
                                                                                                >
                                                                                                    {code}
                                                                                                    {diagnosisItem && ` - ${diagnosisItem.Diagnosis}`}
                                                                                                </button>
                                                                                                {note && !isEditing && (
                                                                                                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#E0F1EE] border-l border-[#B4D9D4] text-gray-700">
                                                                                                        {note}
                                                                                                        <button
                                                                                                            type="button"
                                                                                                            onClick={() => handleRemoveNote(code)}
                                                                                                            title="Remove note"
                                                                                                            className="ml-1 text-gray-500 hover:text-red-600"
                                                                                                        >
                                                                                                            ✕
                                                                                                        </button>
                                                                                                    </span>
                                                                                                )}
                                                                                            </span>
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={() => handleRemoveDiagnosis(code)}
                                                                                                title="Remove diagnosis"
                                                                                                className="text-gray-500 hover:text-red-600 flex-shrink-0"
                                                                                            >
                                                                                                ✕
                                                                                            </button>
                                                                                        </div>

                                                                                        {/* Inline note editor */}
                                                                                        {isEditing && (
                                                                                            <div className="flex flex-col gap-2">
                                                                                                <textarea
                                                                                                    autoFocus
                                                                                                    value={noteDraft}
                                                                                                    onChange={(e) => setNoteDraft(e.target.value)}
                                                                                                    dir={textDirection}
                                                                                                    rows={3}
                                                                                                    placeholder="Add notes for this diagnosis..."
                                                                                                    className="w-full px-3 py-2 border border-blue-500 rounded bg-white text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                                                                                                />
                                                                                                <div className="flex items-center gap-2">
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        onClick={() => handleSaveNote(code)}
                                                                                                        className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                                                                                                    >
                                                                                                        Save
                                                                                                    </button>
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        onClick={handleCancelEditNote}
                                                                                                        className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                                                                                                    >
                                                                                                        Cancel
                                                                                                    </button>
                                                                                                </div>
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    )}

                                                                    {/* Search input */}
                                                                    <div className="relative">
                                                                        <input
                                                                            type="text"
                                                                            placeholder="Search diagnosis codes..."
                                                                            value={diagnosisSearch}
                                                                            onChange={(e) => setPsyDiagnosisSearch(e.target.value)}
                                                                            onFocus={() => setShowDiagnosisDropdown(true)}
                                                                            className="w-full px-3 py-2 border border-[#B4D9D4] rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                                                        />

                                                                        {/* Dropdown */}
                                                                        {showDiagnosisDropdown && diagnosisSearch && (
                                                                            <>
                                                                                {/* Backdrop to close dropdown */}
                                                                                <div
                                                                                    className="fixed inset-0 z-10"
                                                                                    onClick={() => {
                                                                                        setShowDiagnosisDropdown(false);
                                                                                        setPsyDiagnosisSearch('');
                                                                                    }}
                                                                                />

                                                                                {/* Dropdown menu */}
                                                                                <div className="absolute z-20 w-full mt-1 bg-white border border-[#B4D9D4] rounded-lg shadow-lg max-h-64 overflow-y-auto">
                                                                                    {getFilteredDiagnosisCodes().length > 0 ? (
                                                                                        getFilteredDiagnosisCodes().map((diagnosisItem) => (
                                                                                            <div
                                                                                                key={diagnosisItem.code}
                                                                                                onClick={() => handleAddDiagnosis(diagnosisItem.code)}
                                                                                                className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-200 last:border-b-0"
                                                                                            >
                                                                                                <div className="text-sm font-medium text-gray-900">
                                                                                                    {diagnosisItem.code}
                                                                                                </div>
                                                                                                <div className="text-xs text-gray-600">
                                                                                                    {diagnosisItem.Diagnosis}
                                                                                                </div>
                                                                                            </div>
                                                                                        ))
                                                                                    ) : (
                                                                                        <div className="px-3 py-2 text-sm text-gray-500">
                                                                                            No diagnosis codes found
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            </>
                                                                        )}
                                                                    </div>

                                                                </div>

                                                                {/* Medications Section - Using Reusable Component */}
                                                                <MedicationSection
                                                                    medications={psyMedications}
                                                                    setMedications={setPsyMedications}
                                                                    medicationTypes={medicationTypes}
                                                                    medicationsEnabled={medicationsEnabled}
                                                                    setMedicationsEnabled={setMedicationsEnabled}
                                                                    onMedicationsChange={() => { }}
                                                                    showToggle={true}
                                                                    clinicId={patientData?.clinic_id}
                                                                />

                                                                {/* Follow-up and Clinic Assignment Section */}
                                                                <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                                                                    {/* Need Follow Up */}
                                                                    <div>
                                                                        <label className="block text-xs font-medium text-gray-700 mb-2">
                                                                            Need Follow Up
                                                                        </label>
                                                                        <div className="flex gap-6">
                                                                            <label className="flex items-center gap-2 cursor-pointer">
                                                                                <input
                                                                                    type="radio"
                                                                                    name="needFollowUp"
                                                                                    value="yes"
                                                                                    checked={needFollowUp === 'yes'}
                                                                                    onChange={() => {
                                                                                        setNeedFollowUp('yes');
                                                                                        // Follow-up weeks is mandatory when follow-up is needed - default to 8.
                                                                                        if (followUpWeeks === 0) {
                                                                                            setFollowUpWeeks(8);
                                                                                        }
                                                                                        setFollowUpValidationError(null);
                                                                                    }}
                                                                                    className="w-4 h-4"
                                                                                />
                                                                                <span className="text-sm text-gray-700">Yes</span>
                                                                            </label>
                                                                            <label className="flex items-center gap-2 cursor-pointer">
                                                                                <input
                                                                                    type="radio"
                                                                                    name="needFollowUp"
                                                                                    value="no"
                                                                                    checked={needFollowUp === 'no'}
                                                                                    onChange={() => {
                                                                                        setNeedFollowUp('no');
                                                                                        setFollowUpValidationError(null);
                                                                                    }}
                                                                                    className="w-4 h-4"
                                                                                />
                                                                                <span className="text-sm text-gray-700">No</span>
                                                                            </label>
                                                                        </div>
                                                                    </div>

                                                                    {/* Validation Error Message */}
                                                                    {followUpValidationError && (
                                                                        <div className="text-red-600 text-xs font-medium">
                                                                            {followUpValidationError}
                                                                        </div>
                                                                    )}

                                                                    {/* Doctor Follow Up meeting in (Only show if "Yes" is selected) */}
                                                                    {needFollowUp === 'yes' && (
                                                                        <div className="flex items-center gap-3">
                                                                            <label className="text-xs font-medium text-gray-700 whitespace-nowrap">
                                                                                Doctor Follow Up meeting in
                                                                            </label>
                                                                            <input
                                                                                type="number"
                                                                                min="0"
                                                                                value={followUpWeeks}
                                                                                onChange={(e) => setFollowUpWeeks(Math.max(0, parseInt(e.target.value) || 0))}
                                                                                className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                                                                            />
                                                                            <span className="text-sm text-gray-700 whitespace-nowrap">Weeks</span>
                                                                        </div>
                                                                    )}

                                                                    {/* Skip Case Manager and Clinic Assignment (Only show if weeks > 0) */}
                                                                    {needFollowUp === 'yes' && followUpWeeks > 0 && (
                                                                        <>
                                                                            {/* "Skip Case Manager meeting" checkbox hidden from the psychiatrist follow-up UI. skipCaseManager stays at its default so skip_casemanger_followup is submitted as usual. */}

                                                                            <div className="flex items-center gap-3">
                                                                                <label className="text-xs font-medium text-gray-700 whitespace-nowrap">
                                                                                    Clinic Assignment
                                                                                </label>
                                                                                <select
                                                                                    value={clinicAssignment}
                                                                                    onChange={(e) => setClinicAssignment(e.target.value)}
                                                                                    className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                                >
                                                                                    {selectedMeeting?.clinic_assignment_options?.map((clinic: string) => (
                                                                                        <option key={clinic} value={clinic}>
                                                                                            {clinic}
                                                                                        </option>
                                                                                    ))}
                                                                                </select>
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Bottom Bar - Action Buttons */}
                                                            <MeetingActionBar
                                                                meetingTypeId={selectedMeeting?.meetingtype_id}
                                                                meeting={{
                                                                    id: selectedMeetingDetailsId,
                                                                    team_url: selectedMeeting?.team_url,
                                                                    status: selectedMeeting?.status,
                                                                    re_inatke: selectedMeeting?.re_inatke,
                                                                }}
                                                                onSave={handlePSYSave}
                                                                onFinish={handlePSYFinish}
                                                                onNoShow={() => setIsNoShowConfirmationOpen(true)}
                                                                onFinishTreatment={() => setIsFinishTreatmentModalOpen(true)}
                                                                onSendMeetingLink={async () => {
                                                                    const token = localStorage.getItem("token") || "";
                                                                    try {
                                                                        setNotification({ type: 'success', text: 'Sending team link...' });
                                                                        await apiService.sendMeetingLink(token, selectedMeetingDetailsId);
                                                                        setNotification({ type: 'success', text: 'Team link sent successfully!' });
                                                                        setTimeout(() => setNotification(null), 3000);
                                                                    } catch (error: any) {
                                                                        console.error("Failed to send team link:", error);
                                                                        setNotification({ type: 'error', text: error.message || 'Failed to send team link' });
                                                                        setTimeout(() => setNotification(null), 3000);
                                                                    }
                                                                }}
                                                                onRequestDocuments={() => setIsRequestDocumentsModalOpen(true)}
                                                                onCall={() => setMeetingForCall({ id: selectedMeetingDetailsId!, patient_id: Number(patientId), patient_name: patientData?.patient_name, patient_phone: patientData?.phone })}                                                                isSaving={isPsySaving}
                                                                isFinishing={isPsyFinishing}
                                                                additionalButtons={
                                                                    selectedMeeting?.status === 'done' ? (
                                                                        <button
                                                                            onClick={() => {
                                                                                setSelectedMeetingId(selectedMeetingDetailsId);
                                                                                setIsPSYSummaryModalOpen(true);
                                                                            }}
                                                                            className="px-4 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                                                                        >
                                                                            View Summary
                                                                        </button>
                                                                    ) : null
                                                                }
                                                                rightContent={
                                                                    <div className={`text-xs flex-shrink-0 mr-2 truncate ${selectedMeeting?.Severe ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                                                                        {selectedMeeting?.meeting_address || '-'}
                                                                    </div>
                                                                }
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <p className="text-xs text-gray-500 italic">No PSY meeting details available</p>
                                            )
                                        ) : (
                                            <p className="text-xs text-gray-500 italic">Meeting details content coming soon...</p>
                                        )}
                                    </div>
                                );
                            }
                            return <p className="text-gray-500 italic">Meeting not found</p>;
                        })() : (
                            <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 w-full">
                                {selectedSummaryText && (
                                    <div className="text-gray-700 whitespace-pre-wrap text-sm mr-4 break-words" dir={textDirection}>
                                        {selectedSummaryText}
                                    </div>
                                )}
                                {!selectedSummaryText && patientData.meetings_summary?.text && (
                                    <div className="text-gray-700 whitespace-pre-wrap text-sm mr-4 break-words" dir={textDirection}>
                                        {patientData.meetings_summary.text}
                                    </div>
                                )}
                                {!selectedSummaryText && !patientData.meetings_summary?.text && (
                                    <p className="text-gray-500 italic">No meeting summary available</p>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* DRAGGABLE DIVIDER - Between panes */}
                {!isRightPanelCollapsed && (
                    <div
                        className="w-1 bg-gray-200 hover:bg-blue-400 cursor-col-resize transition-colors"
                        onMouseDown={() => setIsDraggingPaneDivider(true)}
                        title="Drag to resize panes"
                    />
                )}

                {/* RIGHT SIDE - Patient Details with Icon Tabs */}
                <div className={`bg-white rounded-lg shadow relative transition-all duration-300 h-full ${isRightPanelCollapsed ? 'flex-[0_0_60px] max-w-[60px]' : 'flex-1 min-w-0'
                    }`}>
                    {/* Toggle Button */}
                    <button
                        onClick={() => setIsRightPanelCollapsed(!isRightPanelCollapsed)}
                        className="absolute left-[-12px] top-6 w-6 h-6 rounded-full bg-white border-2 border-gray-300 cursor-pointer flex items-center justify-center text-gray-700 text-lg z-20 hover:bg-gray-50 hover:border-gray-400 transition-colors shadow-sm"
                        title={isRightPanelCollapsed ? 'Expand panel' : 'Collapse panel'}
                    >
                        {isRightPanelCollapsed ? '‹' : '›'}
                    </button>

                    {/* Right Pane Container - Flex Column */}
                    <div className={`flex flex-col h-full w-full overflow-hidden ${isRightPanelCollapsed ? 'hidden' : ''}`}>
                        {/* Header with RTL Tabs */}
                        <div className="flex flex-col h-full w-full pl-0 pr-3 py-0.5 border-b border-gray-200 overflow-x-hidden ml-1.5 mr-1">
                            <div className="flex items-center justify-between gap-2">
                                {/* Edit Icon on the left */}
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setIsEditPatientModalOpen(true)}
                                        className="p-1 rounded transition-colors text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                                        title="Edit Patient"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={() => setIsViewMeetingsModalOpen(true)}
                                        className="p-1 rounded transition-colors text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                                        title="View Meetings"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={() => setIsCreateTaskModalOpen(true)}
                                        className="p-1 rounded transition-colors text-green-500 hover:text-green-700 hover:bg-green-50"
                                        title="New Task"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                        </svg>
                                    </button>
                                    <button
                                        data-ai-id="toggle-complex-patient-flag"
                                        onClick={() => setIsRedFlagModalOpen(true)}
                                        className={`p-1 rounded transition-colors ${patientData?.red_flag
                                            ? 'text-red-500 bg-gray-100 hover:bg-gray-200'
                                            : 'text-gray-400 hover:bg-gray-100'
                                            }`}
                                        title={patientData?.red_flag ? "Complex patient flagged" : "Flag as complex patient"}
                                    >
                                        {patientData?.red_flag ? (
                                            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                                                <path fillRule="evenodd" d="M3 2.25a.75.75 0 01.75.75v.54l1.838-.46a9.75 9.75 0 016.725.738l.108.054a8.25 8.25 0 005.58.652l3.109-.732a.75.75 0 01.917.81 47.784 47.784 0 00.005 10.337.75.75 0 01-.574.812l-3.114.733a9.75 9.75 0 01-6.594-.77l-.108-.054a8.25 8.25 0 00-5.69-.625l-2.202.55V21a.75.75 0 01-1.5 0V3A.75.75 0 013 2.25z" clipRule="evenodd" />
                                            </svg>
                                        ) : (
                                            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 3v1.5M3 21v-6m0 0l2.77-.693a9 9 0 016.208.682l.108.054a9 9 0 006.086.71l3.114-.732a48.524 48.524 0 01-.005-10.499l-3.11.732a9 9 0 01-6.085-.711l-.108-.054a9 9 0 00-6.208-.682L3 4.5M3 15V4.5" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                                {/* Tab Icons in RTL order (right to left) */}
                                <div className="flex items-center gap-2 mr-3">
                                    {patientData?.suicidal_quest_filled && (
                                        <button
                                            onClick={() => setActiveTab('suicidal-history')}
                                            className={`p-1 rounded transition-colors ${activeTab === 'suicidal-history'
                                                ? 'bg-gray-200 text-gray-900'
                                                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                                                }`}
                                            title="Suicidal Questionnaires"
                                        >
                                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <circle cx="12" cy="12" r="10" strokeWidth="2" />
                                                <text x="12" y="16" fontSize="12" fontWeight="bold" textAnchor="middle" fill="currentColor" stroke="none">S</text>
                                            </svg>
                                        </button>
                                    )}
                                    {/* Medical Opinions (מכתב לגורם חיצוני). Maccabi patients with a done psychiatric
                                        intake only - the backend decides, we just consume the flag. Uses
                                        badge-check rather than document-text, which is already the Files icon. */}
                                    {patientData?.show_medical_opinions && (
                                        <button
                                            onClick={() => setActiveTab('medical-opinions')}
                                            className={`p-1 rounded transition-colors relative ${activeTab === 'medical-opinions'
                                                ? 'bg-gray-200 text-gray-900'
                                                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                                                }`}
                                            title="Medical Opinions"
                                        >
                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                                            </svg>
                                            {hasOpenMedicalOpinion && (
                                                <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full"></span>
                                            )}
                                        </button>
                                    )}
                                    <button
                                        onClick={() => setActiveTab('patient-history')}
                                        className={`p-1 rounded transition-colors ${activeTab === 'patient-history'
                                            ? 'bg-gray-200 text-gray-900'
                                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                                            }`}
                                        title="Patient History"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('questionnaires')}
                                        className={`p-1 rounded transition-colors ${activeTab === 'questionnaires'
                                            ? 'bg-gray-200 text-gray-900'
                                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                                            }`}
                                        title="Questionnaires"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('files')}
                                        className={`p-1 rounded transition-colors relative ${activeTab === 'files'
                                            ? 'bg-gray-200 text-gray-900'
                                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                                            }`}
                                        title="Files"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        {/* Red dot indicator for new clinical documents */}
                                        {patientData?.issues?.some((issue) => issue.issue === 'New Clinical Documents Uploaded') && (
                                            <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                                        )}
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('meeting')}
                                        className={`p-1 rounded transition-colors ${activeTab === 'meeting'
                                            ? 'bg-gray-200 text-gray-900'
                                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                                            }`}
                                        title="Meeting"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('about')}
                                        className={`p-1 rounded transition-colors ${activeTab === 'about'
                                            ? 'bg-gray-200 text-gray-900'
                                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                                            }`}
                                        title="About"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            {/* Tab Content */}
                            <div className="flex-1 overflow-y-auto overflow-x-hidden pl-0 pr-3 pb-4 pt-2 w-full ml-1.5 mr-1">
                                {/* About Tab */}
                                {activeTab === 'about' && (
                                    <div className="space-y-3">
                                        {/* Status and Warnings Row */}
                                        <div className="grid grid-cols-2 gap-0">
                                            {/* Status Field */}
                                            <div className="border border-gray-300 rounded-l px-3 py-1 bg-gray-50 flex items-center gap-2">
                                                <label className="text-xs font-bold text-gray-700 shrink-0">Status:</label>
                                                <p
                                                    className="text-xs font-normal text-gray-900 flex-1"
                                                    title={patientData.status?.toLowerCase()?.includes('finish') ? [patientData.cancel_reason && `Reason: ${patientData.cancel_reason}`, patientData.cancel_notes && `Notes: ${patientData.cancel_notes}`].filter(Boolean).join('\n') || undefined : undefined}
                                                >
                                                    {patientData.status || "-"}
                                                </p>
                                            </div>

                                            {/* Warnings Field */}
                                            {(() => {
                                                const issues = patientData.issues || [];
                                                const issueCount = issues.length;

                                                // Check for Suicidal severe issue
                                                const hasSuicidalSevere = issues.some(i => i.issue.toLowerCase().includes('suicidal severe'));

                                                // Determine the highest severity
                                                const hasSevere = issues.some(i => i.issue_severity.toLowerCase() === 'severe');
                                                const hasError = issues.some(i => i.issue_severity.toLowerCase() === 'error');
                                                const hasWarning = issues.some(i => i.issue_severity.toLowerCase() === 'warning');

                                                let bgColor = 'bg-blue-50';
                                                let borderColor = 'border-blue-300';
                                                let textColor = 'text-blue-900';

                                                if (hasSevere) {
                                                    bgColor = 'bg-red-100';
                                                    borderColor = 'border-red-500';
                                                    textColor = 'text-red-700';
                                                } else if (hasError) {
                                                    bgColor = 'bg-red-50';
                                                    borderColor = 'border-red-300';
                                                    textColor = 'text-red-600';
                                                } else if (hasWarning) {
                                                    bgColor = 'bg-orange-50';
                                                    borderColor = 'border-orange-300';
                                                    textColor = 'text-orange-700';
                                                }

                                                let displayText = '';
                                                let labelText = 'Warnings';
                                                if (issueCount === 0) {
                                                    displayText = '-';
                                                    bgColor = 'bg-gray-50';
                                                    borderColor = 'border-gray-300';
                                                    textColor = 'text-gray-900';
                                                } else if (issueCount === 1) {
                                                    labelText = '';
                                                    displayText = issues[0].issue;
                                                } else if (hasSuicidalSevere) {
                                                    // If Suicidal severe exists, display it instead of "Severe: {count}"
                                                    labelText = '';
                                                    displayText = 'Suicidal severe';
                                                } else {
                                                    // Multiple issues - show highest severity in plural and count
                                                    labelText = '';
                                                    let severityPlural = 'Warnings';
                                                    if (hasSevere) {
                                                        severityPlural = 'Severe';
                                                    } else if (hasError) {
                                                        severityPlural = 'Errors';
                                                    }
                                                    displayText = `${severityPlural}: ${issueCount}`;
                                                }

                                                return (
                                                    <div
                                                        className={`border ${borderColor} rounded-r px-3 py-1 ${bgColor} flex items-center gap-2 ${issueCount > 0 ? 'cursor-pointer hover:opacity-80' : ''}`}
                                                        onClick={() => {
                                                            if (issueCount > 0) {
                                                                setIsIssuesModalOpen(true);
                                                            }
                                                        }}
                                                    >
                                                        <label className={`text-xs font-medium ${textColor} ${issueCount > 0 ? 'cursor-pointer' : ''}`}>
                                                            {labelText}
                                                        </label>
                                                        <p className={`text-sm font-medium ${textColor} ${issueCount > 0 ? 'cursor-pointer' : ''}`}>{displayText}</p>
                                                    </div>
                                                );
                                            })()}
                                        </div>


                                        {/* Patient and Parent Information */}
                                        <div>
                                            {/* Patient Basic Information */}
                                            <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
                                                <div className="pl-3 pr-4 py-3">
                                                    <div className="grid grid-cols-2 gap-3">
                                                        {/* Row 1: ID | Gender */}
                                                        <div className="flex items-center gap-1">
                                                            <label className="text-xs font-normal text-gray-500">Id:</label>
                                                            <p className="text-xs font-bold text-gray-900 truncate" title={patientData.patient_id || "-"}>{patientData.patient_id || "-"}</p>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <label className="text-xs font-normal text-gray-500">Gender:</label>
                                                            <p className="text-xs font-bold text-gray-900 truncate" title={patientData.gender || "-"}>{patientData.gender || "-"}</p>
                                                        </div>

                                                        {/* Row 2: Marital Status | DOB */}
                                                        <div className="flex items-center gap-1">
                                                            <label className="text-xs font-normal text-gray-500">Marital Status:</label>
                                                            <p className="text-xs font-bold text-gray-900 truncate" title={patientData.parents_marital_status || "-"}>{patientData.parents_marital_status || "-"}</p>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <label className="text-xs font-normal text-gray-500">Dob:</label>
                                                            <p className="text-xs font-bold text-gray-900 truncate" title={formatDate(new Date(patientData.DOB).getTime())}>{formatDate(new Date(patientData.DOB).getTime())}</p>
                                                        </div>

                                                        {/* Row 3: Children | Age */}
                                                        <div className="flex items-center gap-1">
                                                            <label className="text-xs font-normal text-gray-500">Children:</label>
                                                            <p className="text-xs font-bold text-gray-900 truncate" title={String(patientData.children || "0")}>{patientData.children || "0"}</p>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <label className="text-xs font-normal text-gray-500">Age:</label>
                                                            <p className="text-xs font-bold text-gray-900 truncate" title={patientData.age || "-"}>{patientData.age || "-"}</p>
                                                        </div>

                                                        {/* Row 4: Email | Phone */}
                                                        <div className="flex items-center gap-1">
                                                            <label className="text-xs font-normal text-gray-500">Email:</label>
                                                            <p className="text-xs font-bold text-gray-900 truncate" title={patientData.email || "-"}>
                                                                {patientData.email || "-"}
                                                            </p>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <label className="text-xs font-normal text-gray-500">Phone:</label>
                                                            <p className="text-xs font-bold text-gray-900 truncate" title={patientData.phone || "-"}>{patientData.phone || "-"}</p>
                                                        </div>

                                                        {/* Row 5: Move To Adult - only once the transfer has started.
                                                            The copy button appears iff the backend supplied a link,
                                                            which it does only while the questionnaire is outstanding. */}
                                                        {patientData.move_youth_to_adult_status && (
                                                            <div className="flex items-center gap-1 col-span-2">
                                                                <label className="text-xs font-normal text-gray-500">Move To Adult:</label>
                                                                <p className="text-xs font-bold text-gray-900 truncate" title={patientData.move_youth_to_adult_status}>
                                                                    {patientData.move_youth_to_adult_status}
                                                                </p>
                                                                {patientData.youth_transfer_link && (
                                                                    <button
                                                                        data-ai-id="patient-copy-youth-transfer-link"
                                                                        onClick={() => {
                                                                            navigator.clipboard.writeText(patientData.youth_transfer_link);
                                                                            setYouthLinkCopied(true);
                                                                            setTimeout(() => setYouthLinkCopied(false), 3000);
                                                                        }}
                                                                        className="text-xs text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none shrink-0"
                                                                        title={patientData.youth_transfer_link}
                                                                    >
                                                                        {youthLinkCopied ? "Copied" : "Copy Questionnaire Link"}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Parent Sections - Only for Youth */}
                                            {patientData.patient_type === 'Youth' && (
                                                <>
                                                    {/* Parent 1 Section */}
                                                    <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
                                                        <div className="px-4 py-2 bg-gray-50 border-b border-gray-300">
                                                            <h3 className="text-sm font-semibold text-gray-900 text-center">Parent 1</h3>
                                                        </div>
                                                        <div className="pl-3 pr-4 py-3">
                                                            <div className="grid grid-cols-2 gap-3">
                                                                {/* Name | Gender */}
                                                                <div className="flex items-center gap-1">
                                                                    <label className="text-xs font-normal text-gray-500">Name:</label>
                                                                    <p className="text-xs font-bold text-gray-900 truncate" title={patientData.parent_1_name || "-"}>{patientData.parent_1_name || "-"}</p>
                                                                </div>
                                                                <div className="flex items-center gap-1">
                                                                    <label className="text-xs font-normal text-gray-500">Gender:</label>
                                                                    <p className="text-xs font-bold text-gray-900 truncate" title={patientData.parent_1_gender || "-"}>{patientData.parent_1_gender || "-"}</p>
                                                                </div>
                                                                {/* Email | Phone */}
                                                                <div className="flex items-center gap-1">
                                                                    <label className="text-xs font-normal text-gray-500">Email:</label>
                                                                    <p className="text-xs font-bold text-gray-900 truncate" title={patientData.parent_1_email || "-"}>
                                                                        {patientData.parent_1_email || "-"}
                                                                    </p>
                                                                </div>
                                                                <div className="flex items-center gap-1">
                                                                    <label className="text-xs font-normal text-gray-500">Phone:</label>
                                                                    <p className="text-xs font-bold text-gray-900 truncate" title={patientData.parent_1_phone_number || "-"}>{patientData.parent_1_phone_number || "-"}</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Parent 2 Section */}
                                                    <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
                                                        <div className="px-4 py-2 bg-gray-50 border-b border-gray-300">
                                                            <h3 className="text-sm font-semibold text-gray-900 text-center">Parent 2</h3>
                                                        </div>
                                                        <div className="pl-3 pr-4 py-3">
                                                            <div className="grid grid-cols-2 gap-3">
                                                                {/* Name | Gender */}
                                                                <div className="flex items-center gap-1">
                                                                    <label className="text-xs font-normal text-gray-500">Name:</label>
                                                                    <p className="text-xs font-bold text-gray-900 truncate" title={patientData.parent_2_name || "-"}>{patientData.parent_2_name || "-"}</p>
                                                                </div>
                                                                <div className="flex items-center gap-1">
                                                                    <label className="text-xs font-normal text-gray-500">Gender:</label>
                                                                    <p className="text-xs font-bold text-gray-900 truncate" title={patientData.parent_2_gender || "-"}>{patientData.parent_2_gender || "-"}</p>
                                                                </div>
                                                                {/* Email | Phone */}
                                                                <div className="flex items-center gap-1">
                                                                    <label className="text-xs font-normal text-gray-500">Email:</label>
                                                                    <p className="text-xs font-bold text-gray-900 truncate" title={patientData.parent_2_email || "-"}>
                                                                        {patientData.parent_2_email || "-"}
                                                                    </p>
                                                                </div>
                                                                <div className="flex items-center gap-1">
                                                                    <label className="text-xs font-normal text-gray-500">Phone:</label>
                                                                    <p className="text-xs font-bold text-gray-900 truncate" title={patientData.parent_2_phone_number || "-"}>{patientData.parent_2_phone_number || "-"}</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </>
                                            )}

                                            {/* Location and Clinic Information */}
                                            <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
                                                <div className="pl-3 pr-4 py-3">
                                                    <div className="grid grid-cols-2 gap-3">
                                                        {/* Row 1: Address | Current Place */}
                                                        <div className="flex items-center gap-1">
                                                            <label className="text-xs font-normal text-gray-500">Address:</label>
                                                            <p className="text-xs font-bold text-gray-900 truncate" dir={textDirection} title={patientData.address || "-"}>{patientData.address || "-"}</p>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <label className="text-xs font-normal text-gray-500">Current Place:</label>
                                                            <p className="text-xs font-bold text-gray-900 truncate" dir={textDirection} title={patientData.current_place || "-"}>{patientData.current_place || "-"}</p>
                                                        </div>

                                                        {/* Row 2: Clinic | Sub Clinic */}
                                                        <div className="flex items-center gap-1">
                                                            <label className="text-xs font-normal text-gray-500">Clinic:</label>
                                                            <p className="text-xs font-bold text-gray-900 truncate" title={(() => {
                                                                const clinic = clinics.find((c: any) => c.id === patientData.clinic_id);
                                                                return clinic?.name || "-";
                                                            })()}>{(() => {
                                                                const clinic = clinics.find((c: any) => c.id === patientData.clinic_id);
                                                                return clinic?.name || "-";
                                                            })()}</p>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <label className="text-xs font-normal text-gray-500">Sub Clinic:</label>
                                                            <p className="text-xs font-bold text-gray-900 truncate" dir={textDirection} title={patientData.sub_clinic || "-"}>{patientData.sub_clinic || "-"}</p>
                                                        </div>

                                                        {/* Row 3: City | HMO */}
                                                        <div className="flex items-center gap-1">
                                                            <label className="text-xs font-normal text-gray-500">City:</label>
                                                            <p className="text-xs font-bold text-gray-900 truncate" dir={textDirection} title={patientData.city || "-"}>{patientData.city || "-"}</p>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <label className="text-xs font-normal text-gray-500">HMO:</label>
                                                            <p className="text-xs font-bold text-gray-900 truncate" title={patientData.HMO || "-"}>{patientData.HMO || "-"}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {(() => {
                                                const selectedMeeting = selectedMeetingDetailsId ? meetings.find(m => m.id === selectedMeetingDetailsId) : null;
                                                const shouldHide = selectedMeeting && selectedMeeting.meetingtype_id === 1;

                                                if (shouldHide) return null;

                                                // Parse Diagnosis_text as a JSON map { "<code>": "note", ... },
                                                // matching the PSY meeting format. Ignore invalid JSON.
                                                let diagnosisTextMap: Record<string, string> = {};
                                                try {
                                                    const parsed = JSON.parse(patientData.Diagnosis_text || '{}');
                                                    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                                                        diagnosisTextMap = parsed as Record<string, string>;
                                                    }
                                                } catch {
                                                    diagnosisTextMap = {};
                                                }

                                                return (
                                                    <>
                                                        {/* Diagnosis */}
                                                        <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
                                                            <div className="pl-3 pr-4 py-1">
                                                                {patientData.Diagnosis ? (
                                                                    <div className="flex flex-col gap-2">
                                                                        {patientData.Diagnosis.split(',').map((code, index) => {
                                                                            const trimmedCode = code.trim();
                                                                            if (!trimmedCode) return null;

                                                                            const diagnosisItem = diagnosisCodes?.find(d => d.code === trimmedCode);
                                                                            const note = diagnosisTextMap[trimmedCode] || '';

                                                                            return (
                                                                                <span
                                                                                    key={`${trimmedCode}-${index}`}
                                                                                    className="inline-flex items-stretch text-xs rounded-lg overflow-hidden border border-[#B4D9D4] self-start max-w-full"
                                                                                >
                                                                                    <span className="px-2 py-1 bg-[#B5D9D4] text-gray-900 whitespace-nowrap">
                                                                                        {trimmedCode}
                                                                                        {diagnosisItem && ` - ${diagnosisItem.Diagnosis}`}
                                                                                    </span>
                                                                                    {note && (
                                                                                        <span className="px-2 py-1 bg-[#E0F1EE] border-l border-[#B4D9D4] text-gray-700">
                                                                                            {note}
                                                                                        </span>
                                                                                    )}
                                                                                </span>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                ) : (
                                                                    <p className="text-sm text-gray-900">No diagnosis recorded</p>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Medications Table */}
                                                        <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
                                                            <div className="overflow-x-auto">
                                                                <table className="min-w-full">
                                                                    <thead>
                                                                        <tr className="border-b border-gray-200">
                                                                            <ResizableHeader column="medication" label="Medication" />
                                                                            <ResizableHeader column="dosage" label="Dosage" />
                                                                            <ResizableHeader column="repetition" label="Repetition" />
                                                                            <ResizableHeader column="issueDate" label="Issue Date" />
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {patientData.medications && patientData.medications.length > 0 ? (
                                                                            patientData.medications.map((med: any, index: number) => {
                                                                                // Get medication name from medication_type_id
                                                                                const medType = medicationTypes.find(mt => mt.id === med.medication_type_id);
                                                                                const medName = medType?.name || med.name || "-";

                                                                                return (
                                                                                    <tr key={index} className="border-b border-gray-100">
                                                                                        <td
                                                                                            className="px-4 py-2 text-sm text-gray-900 truncate"
                                                                                            style={columnWidths.medication ? { maxWidth: `${columnWidths.medication}px` } : { maxWidth: '120px' }}
                                                                                            title={medName}
                                                                                        >
                                                                                            {medName}
                                                                                        </td>
                                                                                        <td
                                                                                            className="px-4 py-2 text-sm text-gray-600 truncate"
                                                                                            style={columnWidths.dosage ? { maxWidth: `${columnWidths.dosage}px` } : { maxWidth: '100px' }}
                                                                                            title={`${med.dosage || med.dosage_amount || ""} ${med.unit || med.dosage_unit || ""}`}
                                                                                        >
                                                                                            {med.dosage || med.dosage_amount || ""} {med.unit || med.dosage_unit || ""}
                                                                                        </td>
                                                                                        <td
                                                                                            className="px-4 py-2 text-sm text-gray-600 whitespace-nowrap"
                                                                                            style={columnWidths.repetition ? { maxWidth: `${columnWidths.repetition}px` } : {}}
                                                                                        >
                                                                                            {med.amount || med.freq_amount || ""} {med.amount_per || med.freq_period || ""}
                                                                                        </td>
                                                                                        <td
                                                                                            className="px-4 py-2 text-sm text-gray-600"
                                                                                            style={columnWidths.issueDate ? { maxWidth: `${columnWidths.issueDate}px` } : {}}
                                                                                        >
                                                                                            {med.start_date ? new Date(med.start_date).toLocaleDateString('en-GB') : "-"}
                                                                                        </td>
                                                                                    </tr>
                                                                                );
                                                                            })
                                                                        ) : (
                                                                            <tr>
                                                                                <td colSpan={4} className="px-4 py-8 text-center text-gray-500 text-sm">
                                                                                    No rows found
                                                                                </td>
                                                                            </tr>
                                                                        )}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    </>
                                                );
                                            })()}

                                            {/* Notes */}
                                            <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
                                                <div className="px-4 py-2 bg-gray-50 border-b border-gray-300">
                                                    <h3 className="text-sm font-semibold text-gray-900">Notes</h3>
                                                </div>
                                                <div className="px-4 py-3">
                                                    <PatientNotesList
                                                        patientId={Number(patientData.id)}
                                                        users={users}
                                                        currentUserId={Number(user.id)}
                                                        hasSuicidalQuestionnaire={!!patientData.suicidal_quest_filled}
                                                    />
                                                </div>
                                            </div>

                                            {/* Suicidal Questionnaire - Conditionally shown */}
                                            {patientData.suicidal_quest_filled && (
                                                <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
                                                    <div className="px-4 py-2 bg-gray-50 border-b border-gray-300 flex items-center justify-between">
                                                        <h3 className="text-sm font-semibold text-gray-900">Suicidal Questionnaire</h3>
                                                        <div className="flex gap-2">
                                                            {/* Edit Icon Button */}
                                                            <button
                                                                onClick={() => setIsSuicidalQuestionnaireModalOpen(true)}
                                                                title="Edit Suicidal Questionnaire"
                                                                aria-label="Edit Suicidal Questionnaire"
                                                                className="w-10 h-10 flex items-center justify-center bg-[#b5d9d4] rounded-lg hover:bg-[#a5c9c4] transition-colors"
                                                            >
                                                                <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                                </svg>
                                                            </button>
                                                            {/* User Icon Button */}
                                                            <button
                                                                onClick={() => setIsContactDetailsModalOpen(true)}
                                                                title="Contact Details"
                                                                aria-label="Contact Details"
                                                                className="w-10 h-10 flex items-center justify-center bg-[#b5d9d4] rounded-lg hover:bg-[#a5c9c4] transition-colors"
                                                            >
                                                                <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="px-4 py-3 space-y-2">
                                                        {/* Question 1 */}
                                                        <div className="flex items-start gap-3">
                                                            <input
                                                                type="checkbox"
                                                                checked={patientData.suicidal_quest_q1 || false}
                                                                readOnly
                                                                className="mt-0.5 w-4 h-4 text-[#3a7380] border-gray-300 rounded focus:ring-[#3a7380]"
                                                            />
                                                            <span className="text-xs text-gray-600">Death wish in recent weeks</span>
                                                        </div>
                                                        {/* Question 2 */}
                                                        <div className="flex items-start gap-3">
                                                            <input
                                                                type="checkbox"
                                                                checked={patientData.suicidal_quest_q2 || false}
                                                                readOnly
                                                                className="mt-0.5 w-4 h-4 text-[#3a7380] border-gray-300 rounded focus:ring-[#3a7380]"
                                                            />
                                                            <span className="text-xs text-gray-600">Thoughts or plans to commit suicide in recent weeks</span>
                                                        </div>
                                                        {/* Question 3 */}
                                                        <div className="flex items-start gap-3">
                                                            <input
                                                                type="checkbox"
                                                                checked={patientData.suicidal_quest_q3 || false}
                                                                readOnly
                                                                className="mt-0.5 w-4 h-4 text-[#3a7380] border-gray-300 rounded focus:ring-[#3a7380]"
                                                            />
                                                            <span className="text-xs text-gray-600">In the past, have tried self harm or to commit suicide</span>
                                                        </div>
                                                        {/* Question 4 */}
                                                        <div className="flex items-start gap-3">
                                                            <input
                                                                type="checkbox"
                                                                checked={patientData.suicidal_quest_q4 || false}
                                                                readOnly
                                                                className="mt-0.5 w-4 h-4 text-[#3a7380] border-gray-300 rounded focus:ring-[#3a7380]"
                                                            />
                                                            <span className="text-xs text-gray-600">At present, have thoughts of committing suicide</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}


                                {/* Meeting Tab */}
                                {activeTab === 'meeting' && (
                                    <div>
                                        {/* Meeting History Header */}
                                        <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
                                            <div className="px-4 py-3 flex items-center justify-between">
                                                <h2 className="text-lg font-semibold text-gray-900">Meeting History</h2>
                                                <div className="flex items-center gap-2">
                                                    {patientData?.show_schedule_link && (
                                                        <button
                                                            onClick={async () => {
                                                                try {
                                                                    const res = await apiService.getPatientScheduleLink(token, Number(patientId));
                                                                    if (res?.link) {
                                                                        window.open(res.link, "_blank", "noopener");
                                                                    }
                                                                } catch (e) {
                                                                    console.error("[PatientPage] Failed to get schedule link", e);
                                                                }
                                                            }}
                                                            className="p-2 hover:bg-gray-100 rounded transition-colors"
                                                            title="Open Patient Schedule Page"
                                                        >
                                                            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 14l4 4m0 0v-3m0 3h-3" />
                                                            </svg>
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => setIsAdHocMeetingModalOpen(true)}
                                                        className="p-2 hover:bg-gray-100 rounded transition-colors relative"
                                                        title="Create ad-hoc meeting"
                                                    >
                                                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                        <svg className="w-3 h-3 text-gray-600 absolute top-0 right-0 bg-white rounded-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
                                                        </svg>
                                                    </button>
                                                    {canScheduleMeeting(user.type) && (
                                                    <button
                                                        onClick={() => setIsAddMeetingModalOpen(true)}
                                                        className="p-2 hover:bg-gray-100 rounded transition-colors relative"
                                                        title="Add Calendar Event"
                                                    >
                                                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                        </svg>
                                                        <svg className="w-3 h-3 text-gray-600 absolute top-0 right-0 bg-white rounded-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
                                                        </svg>
                                                    </button>
                                                    )}
                                                    <button
                                                        onClick={() => setIsUploadTranscriptModalOpen(true)}
                                                        className="p-2 hover:bg-gray-100 rounded transition-colors relative"
                                                        title="Upload Transcript"
                                                    >
                                                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                        </svg>
                                                        <svg className="w-3 h-3 text-gray-600 absolute top-0 right-0 bg-white rounded-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Filters Row */}
                                            <div className="px-4 pb-3 flex items-center gap-3">
                                                {/* Therapist Filter */}
                                                <div className="flex-1">
                                                    <select
                                                        value={selectedTherapist}
                                                        onChange={(e) => setSelectedTherapist(e.target.value)}
                                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    >
                                                        <option value="">Therapist</option>
                                                        {users.map((user) => (
                                                            <option key={user.id} value={user.id}>
                                                                {user.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                {/* Meeting Type Filter */}
                                                <div className="flex-1">
                                                    <select
                                                        value={selectedMeetingType}
                                                        onChange={(e) => setSelectedMeetingType(e.target.value)}
                                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    >
                                                        <option value="">Meeting Type</option>
                                                        {meetingTypes.map((type) => (
                                                            <option key={type.id} value={type.id}>
                                                                {type.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>

                                            {/* Table */}
                                            <div className="border-t border-gray-300">
                                                {isMeetingsLoading ? (
                                                    <div className="flex items-center justify-center py-12">
                                                        <div className="text-center">
                                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                                                            <p className="text-gray-600 mt-4 text-sm">Loading meetings...</p>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <table className="min-w-full">
                                                            <thead className="bg-gray-50">
                                                                <tr>
                                                                    <th className="py-2 pl-4 text-left text-sm font-semibold text-gray-700 border-b border-gray-300">Date</th>
                                                                    <th className="py-2 text-left text-sm font-semibold text-gray-700 border-b border-gray-300">Meeting type</th>
                                                                    <th className="py-2 text-left text-sm font-semibold text-gray-700 border-b border-gray-300">Therapist</th>
                                                                    <th className="py-2 text-left text-sm font-semibold text-gray-700 border-b border-gray-300">Summary</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {(() => {
                                                                    // Apply filters
                                                                    const filteredMeetings = meetings.filter((meeting) => {
                                                                        if (selectedTherapist && meeting.user_id.toString() !== selectedTherapist) {
                                                                            return false;
                                                                        }
                                                                        if (selectedMeetingType && meeting.meetingtype_id.toString() !== selectedMeetingType) {
                                                                            return false;
                                                                        }
                                                                        return true;
                                                                    });

                                                                    if (filteredMeetings.length === 0) {
                                                                        return (
                                                                            <tr>
                                                                                <td colSpan={4} className="py-8 text-center text-gray-500 text-sm">
                                                                                    No meetings found
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    }

                                                                    return filteredMeetings.map((meeting, index) => {
                                                                        // Get meeting type name
                                                                        const meetingType = meetingTypes.find(mt => mt.id === meeting.meetingtype_id);
                                                                        let meetingTypeName = meetingType?.name || '-';

                                                                        // Override meeting type name if re_inatke is true
                                                                        if (meeting.re_inatke) {
                                                                            if (meeting.meetingtype_id === 1 || meeting.meetingtype_id === 4) {
                                                                                meetingTypeName = "Case manager Re intake";
                                                                            } else if (meeting.meetingtype_id === 2 || meeting.meetingtype_id === 3) {
                                                                                meetingTypeName = "Psychiatrist Re intake";
                                                                            }
                                                                        }

                                                                        // Get therapist name
                                                                        const therapist = users.find(u => u.id === meeting.user_id);
                                                                        const therapistName = therapist?.name || '-';

                                                                        // Format date and time
                                                                        const meetingDate = formatDate(meeting.date);
                                                                        const meetingDateTime = formatDateTime(meeting.date);
                                                                        const meetingEndTime = formatTime(meeting.end);
                                                                        const meetingTooltip = `${meetingDateTime} - ${meetingEndTime}`;

                                                                        // Determine summary status using the exact logic provided
                                                                        let summaryContent;
                                                                        let summaryTooltip;

                                                                        const summaryValue = meeting.status === 'canceled'
                                                                            ? 'Cancel'
                                                                            : meeting.status === 'noshow'
                                                                                ? 'No Show'
                                                                                : meeting.status === 'done' && (
                                                                                    ((meeting.summary_sent || meeting.summary_ready) && (meeting.meetingtype_id === 2 || meeting.meetingtype_id === 3)) ||
                                                                                    (meeting.summary_approved && meeting.meetingtype_id === 4)
                                                                                )
                                                                                    ? " "
                                                                                    : meeting.status.charAt(0).toUpperCase() + meeting.status.slice(1);

                                                                        // Calculate tooltip based on the provided logic
                                                                        if (meeting.summary_sent) {
                                                                            summaryTooltip = patientData.clinic_id === 3 ? "Clicks Summary" : "Sent";
                                                                        } else if (meeting.summary_ready && (meeting.meetingtype_id === 2 || meeting.meetingtype_id === 3)) {
                                                                            summaryTooltip = "Not Sent";
                                                                        } else if ((meeting.meetingtype_id === 4 || meeting.meetingtype_id === 1) && meeting.summary_approved) {
                                                                            summaryTooltip = "Approved";
                                                                        } else if (summaryValue === 'No Show' || summaryValue === 'Cancel') {
                                                                            const parts = [
                                                                                meeting.reason_of_cancellation && `Cancelled By: ${meeting.reason_of_cancellation}`,
                                                                                meeting.cancel_reason && `Reason: ${meeting.cancel_reason}`,
                                                                                meeting.cancel_reason_additional_text && `Notes: ${meeting.cancel_reason_additional_text}`,
                                                                            ].filter(Boolean);
                                                                            summaryTooltip = parts.length > 0 ? parts.join('\n') : summaryValue;
                                                                        } else {
                                                                            summaryTooltip = summaryValue;
                                                                        }

                                                                        // Handle summary click
                                                                        const handleSummaryClick = () => {
                                                                            // Open PSY summary for meeting types 2 or 3
                                                                            if (meeting.meetingtype_id === 2 || meeting.meetingtype_id === 3) {
                                                                                setSelectedMeetingId(meeting.id);
                                                                                setIsPSYSummaryModalOpen(true);
                                                                            }
                                                                            // Open CM summary for meeting types 1 or 4
                                                                            else if (meeting.meetingtype_id === 1 || meeting.meetingtype_id === 4) {
                                                                                setSelectedMeetingId(meeting.id);
                                                                                setIsCMSummaryModalOpen(true);
                                                                            }
                                                                        };

                                                                        // If summaryValue is a space, show the document icon
                                                                        if (summaryValue === " ") {
                                                                            summaryContent = (
                                                                                <button
                                                                                    data-ai-id="open-meeting-summary"
                                                                                    className="text-blue-600 hover:text-blue-800"
                                                                                    title={summaryTooltip}
                                                                                    onClick={handleSummaryClick}
                                                                                >
                                                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                                                    </svg>
                                                                                </button>
                                                                            );
                                                                        } else {
                                                                            summaryContent = <span className="text-gray-900" title={summaryTooltip}>{summaryValue}</span>;
                                                                        }

                                                                        return (
                                                                            <tr key={meeting.id} className={`border-b border-gray-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-100'} ${meeting.id === selectedMeetingDetailsId ? 'font-bold' : ''}`}>
                                                                                <td className="py-2 pl-4 text-sm">
                                                                                    <span
                                                                                        className="inline-block px-2 py-1 rounded cursor-pointer hover:bg-gray-200 transition-colors text-gray-900"
                                                                                        title={meetingTooltip}
                                                                                        onClick={() => {
                                                                                            setSelectedMeetingDetailsId(meeting.id);
                                                                                            // Update URL with meeting_id
                                                                                            const params = new URLSearchParams(window.location.search);
                                                                                            params.set('meeting_id', meeting.id.toString());
                                                                                            window.history.pushState({}, '', `${window.location.pathname}?${params.toString()}`);
                                                                                        }}
                                                                                    >
                                                                                        {meetingDate}
                                                                                    </span>
                                                                                </td>
                                                                                <td className="py-2 text-sm text-gray-900">{meetingTypeName}</td>
                                                                                <td className="py-2 text-sm text-gray-900">{therapistName}</td>
                                                                                <td className="py-2 text-sm">
                                                                                    <span
                                                                                        className="inline-block px-2 py-1 rounded cursor-pointer hover:bg-gray-200 transition-colors"
                                                                                        onClick={handleSummaryClick}
                                                                                    >
                                                                                        {summaryContent}
                                                                                    </span>
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    });
                                                                })()}
                                                            </tbody>
                                                        </table>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        {/* Meeting Statistics */}
                                        {!isMeetingsLoading && patientData && (() => {
                                            // Use pre-calculated meeting count fields from patient data
                                            // meeting_count_text1: Case Manager Intake
                                            // meeting_count_text2: Psychiatrist Intake
                                            // meeting_count_text3: Psychiatrist Follow-Up
                                            // meeting_count_text4: Case Manager Follow-Up

                                            const caseManagerIntake = patientData.meeting_count_text1 || '0/0';
                                            const psychiatristIntake = patientData.meeting_count_text2 || '0/0';
                                            const psychiatristFollowUp = patientData.meeting_count_text3 || '0/0';
                                            const caseManagerFollowUp = patientData.meeting_count_text4 || '0/0';

                                            return (
                                                <div className="mt-4 bg-white rounded-lg border border-gray-300 overflow-hidden">
                                                    <div className="grid grid-cols-2 gap-0">
                                                        {/* Case Manager Column */}
                                                        <div className="border-r border-gray-300">
                                                            <div className="px-4 py-2 bg-gray-50 border-b border-gray-300">
                                                                <h3 className="text-sm font-semibold text-gray-900 text-center">Case Manager</h3>
                                                            </div>
                                                            <div className="pl-3 pr-4 py-3">
                                                                <div className="flex items-center justify-between mb-2">
                                                                    <span className="text-sm text-gray-700">Intake</span>
                                                                    <span className="text-sm font-semibold text-gray-900">{caseManagerIntake}</span>
                                                                </div>
                                                                <div className="flex items-center justify-between">
                                                                    <span className="text-sm text-gray-700">Follow-Up</span>
                                                                    <span className="text-sm font-semibold text-gray-900">{caseManagerFollowUp}</span>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Psychiatrist Column */}
                                                        <div>
                                                            <div className="px-4 py-2 bg-gray-50 border-b border-gray-300">
                                                                <h3 className="text-sm font-semibold text-gray-900 text-center">Psychiatrist</h3>
                                                            </div>
                                                            <div className="pl-3 pr-4 py-3">
                                                                <div className="flex items-center justify-between mb-2">
                                                                    <span className="text-sm text-gray-700">Intake</span>
                                                                    <span className="text-sm font-semibold text-gray-900">{psychiatristIntake}</span>
                                                                </div>
                                                                <div className="flex items-center justify-between">
                                                                    <span className="text-sm text-gray-700">Follow-Up</span>
                                                                    <span className="text-sm font-semibold text-gray-900">{psychiatristFollowUp}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}

                                {/* Files Tab */}
                                {activeTab === 'files' && (
                                    <div className="flex flex-col">
                                        {/* Header - Sticky */}
                                        <div className="sticky top-[-0.5rem] bg-white z-10 pb-3 border-b border-gray-200 mb-1 -mx-1 px-1 pt-2">
                                            <div className="flex items-center justify-between">
                                                <h3 className="text-base font-semibold text-gray-900">Files</h3>
                                                <div className="flex items-center gap-2">
                                                    {/* View Icon */}
                                                    <button
                                                        onClick={() => setIsViewFilesModalOpen(true)}
                                                        className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                                                        title="View files"
                                                    >
                                                        <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                        </svg>
                                                    </button>
                                                    {/* Plus Icon */}
                                                    <button
                                                        onClick={() => setIsUploadFileModalOpen(true)}
                                                        className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                                                        title="Add file"
                                                    >
                                                        <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Files List - Scrollable */}
                                        <div className="flex-1 overflow-y-auto space-y-2">
                                            {isFilesLoading ? (
                                                <div className="text-center py-8">
                                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                                                    <p className="text-sm text-gray-600 mt-2">Loading files...</p>
                                                </div>
                                            ) : patientFiles.length === 0 ? (
                                                <div className="text-center py-8 text-gray-500">
                                                    <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                    <p className="text-sm">No files available</p>
                                                </div>
                                            ) : (
                                                patientFiles.map((file) => (
                                                    <div key={file.id} className="bg-[#d4e8e5] rounded-xl p-3 flex items-center justify-between">
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center justify-between mb-1">
                                                                <span className="text-sm font-semibold text-gray-900 truncate">
                                                                    {file.file_type}
                                                                </span>
                                                                <span className="text-xs text-gray-600 ml-2 shrink-0">
                                                                    {formatDate(file.created_at)}
                                                                </span>
                                                            </div>
                                                            <div className="text-xs text-gray-700 truncate">
                                                                {file.filename}{file.description ? ` - ${file.description}` : ''}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2 ml-3">
                                                            {/* Edit icon */}
                                                            <button
                                                                onClick={() => handleEditFile(file)}
                                                                className="p-1.5 hover:bg-white/50 rounded transition-colors"
                                                                title="Edit file"
                                                            >
                                                                <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                                </svg>
                                                            </button>
                                                            {/* Download icon */}
                                                            <button
                                                                onClick={() => handleDownloadFile(file.file_id, file.filename)}
                                                                className="p-1.5 hover:bg-white/50 rounded transition-colors"
                                                                title="Download file"
                                                            >
                                                                <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Questionnaires Tab */}
                                {activeTab === 'questionnaires' && (
                                    <div>
                                        {/* Questionnaires Header */}
                                        <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
                                            <div className="px-4 py-3">
                                                <h2 className="text-lg font-semibold text-gray-900">Questionnaires</h2>
                                            </div>

                                            {/* Questionnaires List */}
                                            <div className="border-t border-gray-300 max-h-[calc(100vh-250px)] overflow-y-auto">
                                                {isQuestionnairesLoading ? (
                                                    <div className="flex items-center justify-center py-12">
                                                        <div className="text-center">
                                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                                                            <p className="text-gray-600 mt-4 text-sm">Loading questionnaires...</p>
                                                        </div>
                                                    </div>
                                                ) : questionnaires.length === 0 ? (
                                                    <div className="text-center py-8 text-gray-500">
                                                        <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                                        </svg>
                                                        <p className="text-sm">No questionnaires available</p>
                                                    </div>
                                                ) : (
                                                    <div className="px-4 py-3 space-y-3">
                                                        {questionnaires.map((questionnaire) => {
                                                            // Format date from start_time
                                                            const completedDate = questionnaire.start_time
                                                                ? new Date(questionnaire.start_time).toLocaleDateString('en-GB', {
                                                                    day: '2-digit',
                                                                    month: '2-digit',
                                                                    year: '2-digit'
                                                                })
                                                                : '-';

                                                            // Apply score logic: show "-" if score is "Empty" or "Completed", otherwise show the score
                                                            const displayScore = questionnaire.score === "Empty" || questionnaire.score === "Completed"
                                                                ? "-"
                                                                : questionnaire.score;

                                                            return (
                                                                <div
                                                                    key={questionnaire.id}
                                                                    className="bg-[#c8dfe3] rounded-lg px-4 py-3 flex items-center justify-between"
                                                                >
                                                                    <div className="flex-1">
                                                                        <h3 className="text-base font-semibold text-gray-900">{questionnaire.type}</h3>
                                                                        <p className="text-sm text-gray-700">Completed on: {completedDate}</p>
                                                                    </div>
                                                                    <div className="flex items-center gap-3">
                                                                        <span
                                                                            className="text-sm font-medium text-gray-900"
                                                                            title={questionnaire.text || undefined}
                                                                        >
                                                                            {displayScore}
                                                                        </span>
                                                                        {questionnaire.start_time && (
                                                                            <button
                                                                                onClick={() => {
                                                                                    setSelectedQuestionnaireId(questionnaire.id);
                                                                                    setSelectedQuestionnaireTitle(questionnaire.type);
                                                                                    setIsViewQuestionnaireModalOpen(true);
                                                                                }}
                                                                                className="p-2 hover:bg-white/50 rounded transition-colors"
                                                                                title="View questionnaire"
                                                                            >
                                                                                <svg className="w-5 h-5 text-[#ff8c42]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                                                </svg>
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Medical Opinions Tab */}
                                {activeTab === 'medical-opinions' && (
                                    <div className="flex-1 flex flex-col">
                                        {/* Create new request */}
                                        <div className="flex justify-end mb-2">
                                            <button
                                                onClick={() => {
                                                    setCreateMedicalOpinionError(null);
                                                    setIsCreateMedicalOpinionModalOpen(true);
                                                }}
                                                className="p-1 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                                                title="New Medical Opinion Request"
                                            >
                                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </button>
                                        </div>

                                        <div className="flex-1 flex flex-col border border-gray-300 rounded-lg overflow-hidden">
                                            {isMedicalOpinionsLoading ? (
                                                <div className="flex justify-center items-center py-8">
                                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400"></div>
                                                </div>
                                            ) : medicalOpinionsError ? (
                                                <div className="text-center py-8 text-red-600 text-sm">{medicalOpinionsError}</div>
                                            ) : medicalOpinions.length === 0 ? (
                                                <div className="text-center py-8 text-gray-500">No medical opinion requests</div>
                                            ) : (
                                                <div className="flex-1 overflow-auto">
                                                    <table className="w-full border-collapse text-xs">
                                                        <thead className="bg-gray-100 sticky top-0">
                                                            <tr>
                                                                <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700">Date</th>
                                                                <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700">Type</th>
                                                                <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700">Status</th>
                                                                <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700">Obligation</th>
                                                                <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700">Files</th>
                                                                <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700">Notes</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {medicalOpinions.map((opinion) => (
                                                                <tr
                                                                    key={opinion.id}
                                                                    onClick={() => openMedicalOpinionRow(opinion)}
                                                                    className="hover:bg-gray-50 cursor-pointer"
                                                                >
                                                                    <td className="border border-gray-300 px-3 py-2 text-gray-900">{formatDate(opinion.created_at)}</td>
                                                                    <td className="border border-gray-300 px-3 py-2 text-gray-900">
                                                                        {medicalOpinionTypeLabels[opinion.opinion_type] || opinion.opinion_type}
                                                                    </td>
                                                                    <td className="border border-gray-300 px-3 py-2 text-gray-900">
                                                                        {opinion.heb_status || opinion.status}
                                                                    </td>
                                                                    <td className="border border-gray-300 px-3 py-2 text-gray-900">{opinion.obligation || '-'}</td>
                                                                    <td className="border border-gray-300 px-3 py-2 text-gray-900">
                                                                        {(opinion.files || []).filter((f) => f.file_type === 'input').length}
                                                                    </td>
                                                                    <td className="border border-gray-300 px-3 py-2 text-gray-900" dir={getTextDirection(opinion.initiator_notes || '')}>
                                                                        {opinion.initiator_notes || '-'}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Suicidal Questionnaires History Tab */}
                                {activeTab === 'suicidal-history' && (
                                    <SuicidalHistoryTab
                                        history={suicidalHistory}
                                        isLoading={isSuicidalHistoryLoading}
                                    />
                                )}

                                {/* Patient History Tab */}
                                {activeTab === 'patient-history' && (
                                    <div className="flex-1 flex flex-col">
                                        {/* Rounded box container */}
                                        <div className="flex-1 flex flex-col border border-gray-300 rounded-lg overflow-hidden">
                                            {isPatientHistoryLoading ? (
                                                <div className="flex justify-center items-center py-8">
                                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400"></div>
                                                </div>
                                            ) : patientHistory.length === 0 ? (
                                                <div className="text-center py-8 text-gray-500">No patient history available</div>
                                            ) : (
                                                <div className="flex-1 flex flex-col overflow-hidden">
                                                    {/* Header Table - Fixed at top */}
                                                    <table className="w-full border-collapse flex-shrink-0">
                                                        <thead className="bg-gray-100">
                                                            <tr className="flex bg-gray-100">
                                                                <th
                                                                    className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 select-none relative flex-shrink-0 truncate"
                                                                    style={patientHistoryColumnWidths['date'] ? { width: `${patientHistoryColumnWidths['date']}px`, minWidth: `${patientHistoryColumnWidths['date']}px`, maxWidth: `${patientHistoryColumnWidths['date']}px` } : { width: '150px' }}
                                                                    onClick={() => {
                                                                        if (patientHistorySortColumn === 'date') {
                                                                            setPatientHistorySortDirection(patientHistorySortDirection === 'asc' ? 'desc' : 'asc');
                                                                        } else {
                                                                            setPatientHistorySortColumn('date');
                                                                            setPatientHistorySortDirection('desc');
                                                                        }
                                                                    }}
                                                                >
                                                                    Date {patientHistorySortColumn === 'date' && (patientHistorySortDirection === 'asc' ? '↑' : '↓')}
                                                                    <div
                                                                        onMouseDown={(e) => handleHistoryResizeStart(e, 'date', patientHistoryColumnWidths['date'] || 150)}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-400 bg-gray-300"
                                                                    />
                                                                </th>
                                                                <th
                                                                    className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 select-none relative flex-shrink-0 truncate"
                                                                    style={patientHistoryColumnWidths['user'] ? { width: `${patientHistoryColumnWidths['user']}px`, minWidth: `${patientHistoryColumnWidths['user']}px`, maxWidth: `${patientHistoryColumnWidths['user']}px` } : { width: '150px' }}
                                                                    onClick={() => {
                                                                        if (patientHistorySortColumn === 'user_id') {
                                                                            setPatientHistorySortDirection(patientHistorySortDirection === 'asc' ? 'desc' : 'asc');
                                                                        } else {
                                                                            setPatientHistorySortColumn('user_id');
                                                                            setPatientHistorySortDirection('asc');
                                                                        }
                                                                    }}
                                                                >
                                                                    User {patientHistorySortColumn === 'user_id' && (patientHistorySortDirection === 'asc' ? '↑' : '↓')}
                                                                    <div
                                                                        onMouseDown={(e) => handleHistoryResizeStart(e, 'user', patientHistoryColumnWidths['user'] || 150)}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-400 bg-gray-300"
                                                                    />
                                                                </th>
                                                                <th
                                                                    className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 select-none relative flex-shrink-0"
                                                                    style={patientHistoryColumnWidths['type'] ? { width: `${patientHistoryColumnWidths['type']}px`, minWidth: `${patientHistoryColumnWidths['type']}px`, maxWidth: `${patientHistoryColumnWidths['type']}px` } : { width: '150px' }}
                                                                    onClick={() => {
                                                                        if (patientHistorySortColumn === 'type') {
                                                                            setPatientHistorySortDirection(patientHistorySortDirection === 'asc' ? 'desc' : 'asc');
                                                                        } else {
                                                                            setPatientHistorySortColumn('type');
                                                                            setPatientHistorySortDirection('asc');
                                                                        }
                                                                    }}
                                                                >
                                                                    Type {patientHistorySortColumn === 'type' && (patientHistorySortDirection === 'asc' ? '↑' : '↓')}
                                                                    <div
                                                                        onMouseDown={(e) => handleHistoryResizeStart(e, 'type', patientHistoryColumnWidths['type'] || 150)}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-400 bg-gray-300"
                                                                    />
                                                                </th>
                                                                <th
                                                                    className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 select-none relative flex-1"
                                                                    style={patientHistoryColumnWidths['action'] ? { width: `${patientHistoryColumnWidths['action']}px`, minWidth: `${patientHistoryColumnWidths['action']}px` } : { minWidth: '150px' }}
                                                                    onClick={() => {
                                                                        if (patientHistorySortColumn === 'action') {
                                                                            setPatientHistorySortDirection(patientHistorySortDirection === 'asc' ? 'desc' : 'asc');
                                                                        } else {
                                                                            setPatientHistorySortColumn('action');
                                                                            setPatientHistorySortDirection('asc');
                                                                        }
                                                                    }}
                                                                >
                                                                    Action {patientHistorySortColumn === 'action' && (patientHistorySortDirection === 'asc' ? '↑' : '↓')}
                                                                    <div
                                                                        onMouseDown={(e) => handleHistoryResizeStart(e, 'action', patientHistoryColumnWidths['action'] || 150)}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-400 bg-gray-300"
                                                                    />
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                    </table>

                                                    {/* Body Table - Scrollable */}
                                                    <div className="flex-1 overflow-auto">
                                                        <table className="w-full border-collapse">
                                                            <tbody className="block">
                                                                {(() => {
                                                                    let sortedData = [...patientHistory];
                                                                    sortedData.sort((a, b) => {
                                                                        let aVal: any = a[patientHistorySortColumn as keyof typeof a];
                                                                        let bVal: any = b[patientHistorySortColumn as keyof typeof b];

                                                                        if (typeof aVal === 'string') {
                                                                            aVal = aVal.toLowerCase();
                                                                            bVal = (bVal as string).toLowerCase();
                                                                        }

                                                                        if (patientHistorySortDirection === 'asc') {
                                                                            return aVal > bVal ? 1 : -1;
                                                                        } else {
                                                                            return aVal < bVal ? 1 : -1;
                                                                        }
                                                                    });

                                                                    return sortedData.map((history) => (
                                                                        <tr
                                                                            key={history.id}
                                                                            className="flex hover:bg-gray-50 cursor-pointer"
                                                                            onClick={() => {
                                                                                console.log('[History] Row clicked:', history.id, history);
                                                                                setSelectedPatientHistoryItem(history);
                                                                                setIsPatientHistoryDetailsModalOpen(true);
                                                                            }}
                                                                        >
                                                                            <td className="border border-gray-300 px-3 py-2 text-xs text-gray-700 flex-shrink-0 truncate" style={patientHistoryColumnWidths['date'] ? { width: `${patientHistoryColumnWidths['date']}px`, minWidth: `${patientHistoryColumnWidths['date']}px`, maxWidth: `${patientHistoryColumnWidths['date']}px` } : { width: '150px' }}>
                                                                                {(() => {
                                                                                    const date = new Date(history.date);
                                                                                    const day = String(date.getDate()).padStart(2, '0');
                                                                                    const month = String(date.getMonth() + 1).padStart(2, '0');
                                                                                    const year = date.getFullYear();
                                                                                    const hours = String(date.getHours()).padStart(2, '0');
                                                                                    const minutes = String(date.getMinutes()).padStart(2, '0');
                                                                                    return `${day}/${month}/${year} ${hours}:${minutes}`;
                                                                                })()}
                                                                            </td>
                                                                            <td className="border border-gray-300 px-3 py-2 text-xs text-gray-700 flex-shrink-0 truncate" style={patientHistoryColumnWidths['user'] ? { width: `${patientHistoryColumnWidths['user']}px`, minWidth: `${patientHistoryColumnWidths['user']}px`, maxWidth: `${patientHistoryColumnWidths['user']}px` } : { width: '150px' }}>
                                                                                {users.find(u => u.id === history.user_id)?.name || `User #${history.user_id}`}
                                                                            </td>
                                                                            <td className="border border-gray-300 px-3 py-2 text-xs text-gray-700 flex-shrink-0 truncate" style={patientHistoryColumnWidths['type'] ? { width: `${patientHistoryColumnWidths['type']}px`, minWidth: `${patientHistoryColumnWidths['type']}px`, maxWidth: `${patientHistoryColumnWidths['type']}px` } : { width: '150px' }}>
                                                                                {history.type}
                                                                            </td>
                                                                            <td className="border border-gray-300 px-3 py-2 text-xs text-gray-700 flex-1 truncate" style={patientHistoryColumnWidths['action'] ? { width: `${patientHistoryColumnWidths['action']}px`, minWidth: `${patientHistoryColumnWidths['action']}px` } : { minWidth: '150px' }}>
                                                                                {history.action}
                                                                            </td>
                                                                        </tr>
                                                                    ));
                                                                })()}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                            </div>
                        </div>
                    </div>
                </div >
            </div >
        );
    };

    return (
        <Layout
            userName={user?.name || user?.email || "User"}
            onLogout={onLogout}
            activeMenuItem="patient-page"
            onNavigate={onNavigate}
            institutionId={user.institution_id || 0}
            user={user}
            taskCount={taskCount}
            whatsappUnreadCount={whatsappUnreadCount}
        >
            <div className="flex flex-col h-full overflow-hidden">
                {/* Global Header */}
                <div className="bg-white border-b border-gray-200 flex-shrink-0">
                    <div className="flex justify-between items-center px-0 py-2 mr-[100px]">
                        {/* Left side - Psychiatrist and Case Manager */}
                        <div className="flex items-center gap-6 ml-4">
                            <div>
                                <span className="text-xs font-normal text-gray-500 mr-2">Psychiatrist:</span>
                                <span className="text-sm font-bold text-gray-900">
                                    {users.find(u => u.id === patientData?.doctor_id)?.name || "-"}
                                </span>
                            </div>
                            <div>
                                <span className="text-xs font-normal text-gray-500 mr-2">Case Manager:</span>
                                <span className="text-sm font-bold text-gray-900">
                                    {users.find(u => u.id === patientData?.case_manager_id)?.name || "-"}
                                </span>
                            </div>
                        </div>

                        {/* Right side - Patient Name and HM_ID on one line, bold with gap */}
                        <div className="text-right flex items-center justify-end gap-3">
                            {/* Parent name - only for Youth (Kids/Youth) patients, left of the patient info */}
                            {isYouthPatient && (
                                <div dir="rtl">
                                    <p className="text-base font-bold text-gray-900">
                                        שם הורה: {patientData?.parent_1_name || "-"}
                                    </p>
                                </div>
                            )}
                            <div>
                                <p className="text-base font-bold text-gray-900">
                                    {patientData?.HM_ID || ""}<span className="mx-3">-</span><span dir={textDirection}>{patientData?.patient_name || ""}</span>
                                </p>
                            </div>
                            {patientData?.phone && (
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-bold text-gray-900">{patientData.phone}</span>
                                    <button
                                        data-ai-id="copy-patient-phone"
                                        onClick={() => {
                                            navigator.clipboard.writeText(patientData.phone);
                                        }}
                                        title={`Copy: ${patientData.phone}`}
                                        className="p-2 hover:bg-gray-300 rounded-lg transition-colors flex-shrink-0"
                                    >
                                        <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={() => { setShowWhatsAppPopup(true); setPatientWaUnread(0); }}
                                        title="Open WhatsApp conversation"
                                        className="p-2 hover:bg-green-100 rounded-lg transition-colors flex-shrink-0"
                                        style={{ position: "relative" }}
                                    >
                                        <span style={{ fontSize: 18 }}>💬</span>
                                        {patientWaUnread > 0 && (
                                            <span style={{
                                                position: "absolute",
                                                top: 2,
                                                right: 2,
                                                background: "#ef4444",
                                                color: "#fff",
                                                borderRadius: "50%",
                                                minWidth: 16,
                                                height: 16,
                                                fontSize: 10,
                                                fontWeight: 700,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                padding: "0 3px",
                                                lineHeight: 1,
                                            }}>
                                                {patientWaUnread}
                                            </span>
                                        )}
                                    </button>
                                    {user.mobile_tracking && (
                                        <button
                                            onClick={() => setShowMobileTrackingPopup(true)}
                                            title="Mobile Tracking"
                                            className="p-2 hover:bg-blue-100 rounded-lg transition-colors flex-shrink-0"
                                        >
                                            <span style={{ fontSize: 18 }}>📱</span>
                                        </button>
                                    )}
                                    <button
                                        onClick={() => setShowCallPatientPopup(true)}
                                        title="Call patient"
                                        className="p-2 hover:bg-green-100 rounded-lg transition-colors flex-shrink-0"
                                    >
                                        <svg className="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
                                        </svg>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex-1 overflow-hidden px-0 pt-0">
                    {renderContent()}
                </div>
            </div>

            {/* Issues Modal */}
            {patientData && (
                <IssuesModal
                    isOpen={isIssuesModalOpen}
                    onClose={() => setIsIssuesModalOpen(false)}
                    issues={patientData.issues || []}
                    getUserName={(userId: number) => users.find(u => u.id === userId)?.name || "-"}
                />
            )}

            {/* Edit Patient Modal */}
            {patientData && (
                <EditPatientModal
                    isOpen={isEditPatientModalOpen}
                    onClose={() => {
                        setIsEditPatientModalOpen(false);
                    }}
                    patient={patientData}
                    onSave={async (updatedData: any) => {
                        setIsSavingPatientInfo(true);
                        try {
                            await apiService.updatePatientInfo(
                                token,
                                patientData.id,
                                updatedData
                            );
                            // Refresh patient data
                            const data = await apiService.getPatientPage({
                                token,
                                patient_id: patientId,
                            });
                            setPatientData(data);
                            // Close modal
                            setIsEditPatientModalOpen(false);
                        } catch (error: any) {
                            console.error('Failed to update patient info:', error, 'message:', error.message);
                            alert(error.message || 'Failed to update patient information');
                        } finally {
                            setIsSavingPatientInfo(false);
                        }
                    }}
                    isSaving={isSavingPatientInfo}
                    lang={patientData?.lang}
                />
            )}

            {/* Edit Notes Modal */}
            {patientData && (
                <EditCommentsModal
                    isOpen={isEditNotesModalOpen}
                    onClose={() => {
                        setIsEditNotesModalOpen(false);
                    }}
                    initialNotes={patientData.task || ''}
                    onSave={async (newNotes: string) => {
                        setIsSavingNotes(true);
                        try {
                            await apiService.updatePatientComments(
                                token,
                                patientData.id,
                                newNotes
                            );
                            // Refresh patient data
                            const data = await apiService.getPatientPage({
                                token,
                                patient_id: patientId,
                            });
                            setPatientData(data);
                            // Close modal
                            setIsEditNotesModalOpen(false);
                        } catch (error: any) {
                            console.error('Failed to update notes:', error);
                            throw new Error(error.response?.data?.message || 'Failed to update notes');
                        } finally {
                            setIsSavingNotes(false);
                        }
                    }}
                    isSaving={isSavingNotes}
                />
            )}

            {/* Suicidal Questionnaire Modal */}
            {patientData && (
                <SuicidalQuestionnaireModal
                    isOpen={isSuicidalQuestionnaireModalOpen}
                    onClose={() => setIsSuicidalQuestionnaireModalOpen(false)}
                    patientId={patientData.id}
                    userId={user.id as number}
                    token={token}
                    initialData={{
                        q1: patientData.suicidal_quest_q1 || false,
                        q2: patientData.suicidal_quest_q2 || false,
                        q3: patientData.suicidal_quest_q3 || false,
                        q4: patientData.suicidal_quest_q4 || false,
                    }}
                    onSuccess={async () => {
                        // Refresh patient data after successful update
                        try {
                            const data = await apiService.getPatientPage({
                                token,
                                patient_id: patientId,
                            });
                            setPatientData(data);
                        } catch (error) {
                            console.error('Failed to refresh patient data:', error);
                        }
                    }}
                />
            )}

            {/* Contact Details Modal */}
            {patientData && (
                <ContactDetailsModal
                    isOpen={isContactDetailsModalOpen}
                    onClose={() => setIsContactDetailsModalOpen(false)}
                    contactName={patientData.contact_name || ''}
                    contactPhone={patientData.contact_phone || ''}
                    contactRole={patientData.contact_role || ''}
                />
            )}

            {/* PSY Summary Modal */}
            {patientData && (
                <PSYSummaryModal
                    isOpen={isPSYSummaryModalOpen}
                    onClose={() => {
                        setIsPSYSummaryModalOpen(false);
                        setSelectedMeetingId(undefined);
                        // Clear notification when modal closes
                        setNotification(null);
                    }}
                    meetingId={selectedMeetingId}
                    clinicId={patientData.clinic_id}
                    token={token}
                    medicationTypes={medicationTypes}
                    diagnosisCodes={diagnosisCodes}
                    onSaved={refreshMeetings}
                    onSuccess={async () => {
                        // Navigate to calendar after successful update (ad-hoc meetings
                        // table for an ad-hoc meeting)
                        if (onNavigate) {
                            const finishedMeeting = meetings.find(m => m.id === selectedMeetingId);
                            onNavigate(finishedMeeting?.ad_hoc ? 'adhoc-meetings' : 'calendar');
                        }
                    }}
                    onStatusMessage={(message) => {
                        if (message) {
                            // Show notification
                            setNotification({ type: message.type === 'loading' ? 'success' : message.type, text: message.text });
                            // Auto-clear non-loading messages after 3 seconds
                            if (message.type !== 'loading') {
                                setTimeout(() => setNotification(null), 3000);
                            }
                        } else {
                            // Clear notification
                            setNotification(null);
                        }
                    }}
                    user={user}
                />
            )}

            {/* CM Summary Modal */}
            {patientData && (
                <CMSummaryModal
                    isOpen={isCMSummaryModalOpen}
                    onClose={() => {
                        setIsCMSummaryModalOpen(false);
                        setSelectedMeetingId(undefined);
                        // Clear notification when modal closes
                        setNotification(null);
                    }}
                    meetingId={selectedMeetingId}
                    token={token}
                    onSuccess={refreshMeetings}
                    onStatusMessage={handleCMSummaryStatusMessage}
                />
            )}

            {/* New Meeting Modal */}
            {patientData && (
                <NewMeetingModal
                    isOpen={isAddMeetingModalOpen}
                    onClose={() => setIsAddMeetingModalOpen(false)}
                    patient={patientData}
                    users={users as any}
                    meetingTypes={meetingTypes}
                    currentUser={user}
                />
            )}

            {isAdHocMeetingModalOpen && patientData && (
                <AdHocMeetingModal
                    patientId={Number(patientId)}
                    token={token}
                    meetingTypes={meetingTypes}
                    onClose={() => setIsAdHocMeetingModalOpen(false)}
                    onSuccess={(meetingId) => {
                        setNotification({ type: 'success', text: 'Meeting created successfully!' });
                        setTimeout(() => setNotification(null), 3000);
                        // Switch to the meetings tab (the details panel only renders
                        // there), refresh the list, then open the created meeting.
                        setActiveTab('meeting');
                        refreshMeetings().then(() => {
                            if (meetingId) setSelectedMeetingDetailsId(meetingId);
                        });
                    }}
                    onError={(msg) => {
                        setNotification({ type: 'error', text: msg });
                        setTimeout(() => setNotification(null), 3000);
                    }}
                />
            )}

            {/* Finish Treatment Modal */}
            {patientData && (
                <FinishTreatmentModal
                    isOpen={isFinishTreatmentModalOpen}
                    onClose={() => setIsFinishTreatmentModalOpen(false)}
                    patientId={Number(patientId)}
                    token={token}
                    onSuccess={async () => {
                        // Refetch patient data after finishing treatment
                        try {
                            const data = await apiService.getPatientPage({
                                token,
                                patient_id: patientId,
                            });
                            setPatientData(data);
                        } catch (err: any) {
                            console.error("Failed to reload patient data:", err);
                        }
                    }}
                />
            )}

            {/* View Meetings Modal */}
            {patientData && (
                <ViewMeetingsModal
                    isOpen={isViewMeetingsModalOpen}
                    onClose={() => setIsViewMeetingsModalOpen(false)}
                    patientId={Number(patientId)}
                    patientName={patientData.patient_name}
                    user={user}
                    token={token}
                    users={users}
                    meetingTypes={propsMeetingTypes}
                />
            )}

            {/* Finish Meeting Error Modal */}
            {isFinishErrorModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Error</h2>
                        <p className="text-sm text-red-600 mb-6">{finishErrorMessage}</p>
                        <button
                            onClick={() => setIsFinishErrorModalOpen(false)}
                            className="w-full px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}

            {/* Finish Meeting Warning Modal */}
            {isFinishWarningModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Do you want to continue?</h2>
                        <div className="mb-6">
                            <h3 className="text-sm font-medium text-gray-700 mb-2">Warnings:</h3>
                            <ul className="space-y-2">
                                {finishWarnings.map((warning, idx) => (
                                    <li key={idx} className="text-sm text-gray-600 flex items-start">
                                        <span className="mr-2">•</span>
                                        <span>{warning}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setIsFinishWarningModalOpen(false)}
                                className="flex-1 px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={async () => {
                                    if (!finishWarningMeetingId) return;
                                    try {
                                        console.log('[Meeting] Retrying finish with skip_warnings=true');
                                        const response = await apiService.finishMeeting({
                                            token,
                                            meeting_id: finishWarningMeetingId,
                                            no_transcript: false,
                                            skip_warnings: true,
                                        });
                                        console.log('[Meeting] Meeting finish response:', response.status);

                                        setIsFinishWarningModalOpen(false);

                                        // Handle the retry response
                                        if (response.status === 'OK') {
                                            console.log('[Meeting] Meeting finished successfully');
                                            setNotification({ type: 'success', text: 'Meeting finished successfully' });
                                            setTimeout(() => {
                                                setNotification(null);
                                                const finishedMeeting = meetings.find(m => m.id === finishWarningMeetingId);
                                                onNavigate?.(finishedMeeting?.ad_hoc ? 'adhoc-meetings' : 'calendar');
                                            }, 3000);
                                        } else if (response.status === 'Error') {
                                            console.error('[Meeting] Error finishing meeting:', response.text);
                                            setFinishErrorMessage(response.text);
                                            setIsFinishErrorModalOpen(true);
                                        }
                                    } catch (error: any) {
                                        console.error('[Meeting] Failed to finish meeting:', error);
                                        setFinishErrorMessage(error.message || 'Failed to finish meeting');
                                        setIsFinishErrorModalOpen(true);
                                    }
                                }}
                                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Notification Toast - Bottom Right */}
            {notification && (
                <div
                    className="fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg z-[1400]"
                    style={{
                        backgroundColor:
                            notification.type === 'error' ? '#fee2e2' :
                                '#d1fae5',
                        color:
                            notification.type === 'error' ? '#991b1b' :
                                '#065f46',
                        border: `1px solid ${notification.type === 'error' ? '#fca5a5' :
                            '#6ee7b7'
                            }`
                    }}
                >
                    {notification.text}
                </div>
            )}

            {/* Edit File Modal */}
            <EditFileModal
                isOpen={isEditFileModalOpen}
                onClose={() => setIsEditFileModalOpen(false)}
                file={selectedFile}
                fileTypes={fileTypes}
                onSave={handleSaveFile}
                isSaving={isSavingFile}
            />

            {/* View Files Modal */}
            <ViewFilesModal
                isOpen={isViewFilesModalOpen}
                onClose={() => setIsViewFilesModalOpen(false)}
                files={patientFiles}
                fileTypes={fileTypes}
                token={token}
                onEditFile={(file) => {
                    setSelectedFile(file);
                    setIsEditFileModalOpen(true);
                }}
                onDownloadFile={handleDownloadFile}
                formatDate={formatDate}
            />

            {/* Create Task Modal */}
            <CreateTaskModal
                isOpen={isCreateTaskModalOpen}
                onClose={() => setIsCreateTaskModalOpen(false)}
                patientId={typeof patientId === 'string' ? parseInt(patientId, 10) : patientId}
                users={users}
                patientDoctorId={patientData?.doctor_id}
                patientCaseManagerId={patientData?.case_manager_id}
                meetingTypes={meetingTypes}
            />

            {/* Upload File Modal */}
            <UploadFileModal
                isOpen={isUploadFileModalOpen}
                onClose={() => setIsUploadFileModalOpen(false)}
                fileTypes={fileTypes}
                onUpload={handleUploadFile}
                isUploading={isUploadingFile}
            />

            {/* View Questionnaire Modal */}
            {selectedQuestionnaireId && (
                <ViewQuestionnaireModal
                    isOpen={isViewQuestionnaireModalOpen}
                    onClose={() => {
                        setIsViewQuestionnaireModalOpen(false);
                        setSelectedQuestionnaireId(null);
                        setSelectedQuestionnaireTitle("");
                    }}
                    questionnaireId={selectedQuestionnaireId}
                    questionnaireTitle={selectedQuestionnaireTitle}
                    lang={patientData?.lang}
                />
            )}

            {/* Upload Transcript Modal */}
            <UploadTranscriptModal
                isOpen={isUploadTranscriptModalOpen}
                onClose={() => setIsUploadTranscriptModalOpen(false)}
                meetings={meetings}
                meetingTypes={meetingTypes}
                onUpload={handleUploadTranscript}
                isUploading={isUploadingTranscript}
            />

            {/* No Show Modal */}
            {isNoShowConfirmationOpen && selectedMeetingDetailsId && (() => {
                const selectedMeeting = meetings.find(m => m.id === selectedMeetingDetailsId);
                return selectedMeeting ? (
                    <NoShowModal
                        isOpen={isNoShowConfirmationOpen}
                        onClose={() => setIsNoShowConfirmationOpen(false)}
                        meetingId={selectedMeeting.id}
                        meetingTypeId={selectedMeeting.meetingtype_id}
                        token={token}
                        onSuccess={async () => {
                            console.log('[Meeting] Marked as no-show');
                            setNotification({ type: 'success', text: 'Meeting marked as no-show' });
                            setTimeout(() => {
                                setNotification(null);
                                onNavigate?.('calendar');
                            }, 3000);
                            // Reload meetings to update the list
                            const meetingsData = await apiService.getPatientMeetings(token, patientId);
                            setMeetings(meetingsData);
                        }}
                    />
                ) : null;
            })()}

            <RequestDocumentsModal
                isOpen={isRequestDocumentsModalOpen}
                onClose={() => setIsRequestDocumentsModalOpen(false)}
                token={token}
                onSubmit={handleRequestDocuments}
                preSelectedIds={(() => {
                    if (patientData?.upload_files_status !== 'waiting') return [];
                    const raw = patientData?.upload_files_ids;
                    if (!raw) return [];
                    const arr = typeof raw === 'string' ? JSON.parse(raw) : raw;
                    return (arr as any[]).map(Number);
                })()}
                preSelectedMessage={patientData?.upload_files_status === 'waiting' ? (patientData?.upload_files_message ?? '') : ''}
            />

            {/* Create Medical Opinion Request Modal */}
            <CreateMedicalOpinionModal
                isOpen={isCreateMedicalOpinionModalOpen}
                onClose={() => {
                    setIsCreateMedicalOpinionModalOpen(false);
                    setCreateMedicalOpinionError(null);
                }}
                patientName={patientData?.patient_name || ''}
                // patient_id is the national id (shown as "Id" on the About tab), not the row id.
                patientTz={patientData?.patient_id || ''}
                patientHmId={patientData?.HM_ID || ''}
                clinicName={clinics.find((c: any) => c.id === patientData?.clinic_id)?.name || ''}
                // Prefer the backend-resolved name: the local users list is filtered by
                // institution and often will not contain the patient's doctor.
                doctorName={
                    patientData?.doctor_name
                    || users.find((u) => u.id === patientData?.doctor_id)?.name
                    || ''
                }
                onCreate={handleCreateMedicalOpinion}
                isSaving={isCreatingMedicalOpinion}
                serverError={createMedicalOpinionError}
            />

            {/* Medical Opinion Obligation Approval Modal */}
            <MedicalOpinionObligationApprovalModal
                isOpen={isMedicalOpinionApprovalModalOpen}
                onClose={() => {
                    setIsMedicalOpinionApprovalModalOpen(false);
                    setSelectedMedicalOpinion(null);
                }}
                token={token}
                opinion={selectedMedicalOpinion}
                patientName={patientData?.patient_name || ''}
                patientTz={patientData?.patient_id || ''}
                patientHmId={patientData?.HM_ID || ''}
                patientPhone={patientData?.phone || patientData?.main_phone || ''}
                parent1Phone={patientData?.parent_1_phone_number}
                parent2Phone={patientData?.parent_2_phone_number}
                clinicName={clinics.find((c: any) => c.id === patientData?.clinic_id)?.name || ''}
                isAdmin={isAdmin}
                getUserName={(userId: number) => users.find((u) => u.id === userId)?.name || ''}
                onSuccess={() => setMedicalOpinionsRefreshKey((k) => k + 1)}
            />

            {/* Psychiatric Opinion Letter - the same modal the Letter task opens, reached here
                by clicking an in_progress or completed request. Read-only unless the viewer is
                the assigned doctor and the request is still in progress. */}
            <MedicalOpinionLetterModal
                isOpen={isMedicalOpinionLetterModalOpen}
                onClose={() => {
                    setIsMedicalOpinionLetterModalOpen(false);
                    setSelectedMedicalOpinion(null);
                }}
                token={token}
                opinion={selectedMedicalOpinion}
                patientName={patientData?.patient_name || ''}
                patientTz={patientData?.patient_id || ''}
                patientHmId={patientData?.HM_ID || ''}
                patientPhone={patientData?.phone || patientData?.main_phone || ''}
                parent1Phone={patientData?.parent_1_phone_number}
                parent2Phone={patientData?.parent_2_phone_number}
                clinicName={clinics.find((c: any) => c.id === patientData?.clinic_id)?.name || ''}
                currentUserId={Number(user?.id) || undefined}
                isAdmin={isAdmin}
                diagnosisCodes={diagnosisCodes}
                medicationTypes={medicationTypes}
                getUserName={(userId: number) => users.find((u) => u.id === userId)?.name || ''}
                onSuccess={() => setMedicalOpinionsRefreshKey((k) => k + 1)}
            />

            {/* Red Flag Modal */}
            {patientData && (
                <RedFlagModal
                    isOpen={isRedFlagModalOpen}
                    onClose={() => setIsRedFlagModalOpen(false)}
                    patientId={patientData.id}
                    userId={Number(user.id)}
                    isRedFlagged={!!patientData.red_flag}
                    initialReasons={patientData.red_flag_reason}
                    initialOtherText={patientData.red_flag_other}
                    onSuccess={async () => {
                        const data = await apiService.getPatientPage({
                            token,
                            patient_id: patientId,
                        });
                        setPatientData(data);
                        if (activeTab === 'patient-history') {
                            setPatientHistoryRefreshKey(k => k + 1);
                        }
                    }}
                    onRemoveSuccess={() => {
                        setNotification({ type: 'success', text: 'Complex patient flag removed' });
                        setTimeout(() => setNotification(null), 3000);
                    }}
                />
            )}

            {showWhatsAppPopup && patientData?.phone && (
                <WhatsAppConversationPopup
                    phoneNumber={patientData.phone}
                    patientName={patientData.patient_name || `${patientData.first_name || ""} ${patientData.last_name || ""}`.trim()}
                    patientHmId={patientData.HM_ID}
                    patientId={Number(patientId)}
                    currentUserId={typeof user.id === "number" ? user.id : Number(user.id)}
                    canSendFreeform={true}
                    onClose={() => setShowWhatsAppPopup(false)}
                    onUnreadChange={(count) => setPatientWaUnread(count)}
                />
            )}
            {showMobileTrackingPopup && (
                <MobileTrackingPopup
                    token={localStorage.getItem("token") ?? ""}
                    patientId={Number(patientId)}
                    patientName={patientData?.patient_name || `${patientData?.first_name || ""} ${patientData?.last_name || ""}`.trim() || String(patientId)}
                    onClose={() => setShowMobileTrackingPopup(false)}
                />
            )}
            {meetingForCall && (
                <CallPatientModal
                    meeting={meetingForCall}
                    token={token}
                    onClose={() => setMeetingForCall(null)}
                    onSuccess={() => {
                        setNotification({ type: 'success', text: 'Call initiated successfully!' });
                        setTimeout(() => setNotification(null), 3000);
                    }}
                    onError={(msg) => {
                        setNotification({ type: 'error', text: msg });
                        setTimeout(() => setNotification(null), 3000);
                    }}
                />
            )}
            {showCallPatientPopup && patientData && (
                <CallPatientConfModal
                    patientId={Number(patientId)}
                    patientName={patientData.patient_name}
                    patientPhone={patientData.phone}
                    parent1Phone={patientData.parent_1_phone_number}
                    parent2Phone={patientData.parent_2_phone_number}
                    token={token}
                    onClose={() => setShowCallPatientPopup(false)}
                    onSuccess={() => {
                        setNotification({ type: 'success', text: 'Call initiated successfully!' });
                        setTimeout(() => setNotification(null), 3000);
                    }}
                    onError={(msg) => {
                        setNotification({ type: 'error', text: msg });
                        setTimeout(() => setNotification(null), 3000);
                    }}
                />
            )}
        </Layout>
    );
};
